import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { Container, Typography, Button, Card, CardContent, CardHeader, Box, Grid, Paper, Chip, CircularProgress, Snackbar, TextField, FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, Tab, Tabs, Menu, MenuItem, ButtonGroup } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core/styles';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import { TrendingUp, Description, GpsFixed, People, Visibility, Assessment, BarChart as BarChartIcon, PieChart as PieChartIcon, Home, Person, ShowChart, Public, Videocam, GridOn, GetApp, Schedule, PlayArrow, Error, ArrowDropDown, Email, DesktopWindows, PhoneAndroid, EmojiObjects } from '@material-ui/icons';
import Chart from 'react-apexcharts';
import { DashboardMetricCard } from '../components/DashboardMetricCard';
import ContentThemesTab from '../components/ContentThemesTab';
import InfluencersTab from '../components/InfluencersTab';
import PostsTab from '../components/PostsTab';
import CategoryBreakdownTable from '../components/CategoryBreakdownTable';
import InfluencerOverlapSection from '../components/InfluencerOverlapSection';
import PostFormatAnalysis from '../components/PostFormatAnalysis';
import DemographicHeatTable, { BrandLegend } from '../components/DemographicHeatTable';
import InfluencerCategoryDistribution from '../components/InfluencerCategoryDistribution';
import { formatNumber, formatPercentage, getBrandColor } from '../lib/utils';
import { generateShareOfVoiceInsight, generateBrandComparisonInsight } from '../lib/insights';
import {
  getCombinedBrandListCompetitor,
  creatorTypesWithNormalTypeFetch,
  creatorTypesYoutubeWithNormalTypeFetch,
  getCompetitorInsights,
  downloadNewInstagramData,
  downloadNewYoutubeData,
  generateIntelligenceReport,
  getIntelligenceReportStatus,
  retrieveIntelligenceReport,
  sendIntelligenceReportEmail,
  getAutoTrackSettings,
  saveAutoTrackSettings,
  pauseAutoTrack,
  runAutoTrackNow,
  getAutoTrackHistory,
  getCompetitorAudienceDemographics,
} from 'api_handlers/competitor';
import moment from 'moment';
import { getCurrentUser } from 'api_handlers/auth';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#ffffff',
  },
  content: {
    height: '100vh',
    overflow: 'scroll',
    flexGrow: 1,
    padding: theme.spacing(3),
    paddingTop: theme.spacing(4),
    textAlign: 'left',
    fontFamily:
      'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    boxSizing: 'border-box',
  },
  contentShift: {
    height: '100vh',
    overflow: 'scroll',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    paddingBottom: 0,
    borderBottom: 'none',
  },
  brandFilter: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    paddingTop: theme.spacing(1),
  },
  brandChip: {
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  tabsContainer: {
    marginBottom: theme.spacing(2),
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  navButton: {
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 400,
    minHeight: 48,
    padding: '12px 16px',
    color: '#6b7280',
    borderRadius: '8px',
    backgroundColor: '#f3f4f6',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#e5e7eb',
      color: '#6b7280',
    },
    '& .MuiButton-startIcon': {
      color: '#6b7280',
    },
  },
  navButtonActive: {
    fontWeight: 500,
    color: '#111827',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    '&:hover': {
      backgroundColor: '#ffffff',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    },
    '& .MuiButton-startIcon': {
      color: '#111827',
    },
  },
  card: {
    height: '100%',
    transition: 'all 0.3s ease',
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      boxShadow: theme.shadows[8],
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
  },
  iconContainer: {
    height: 50,
    width: 50,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: theme.shadows[2],
  },
  insightBox: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  insightHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  insightText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  tableContainer: {
    marginTop: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
  },
  tableHeader: {
    backgroundColor: theme.palette.background.default,
  },
  autoTrackHeaderBtnOutlinedMuted: {
    color: '#9ca3af',
    borderColor: '#e5e7eb',
    backgroundColor: '#f9fafb',
    '&:hover': {
      borderColor: '#e5e7eb',
      backgroundColor: '#f9fafb',
    },
    '&.Mui-disabled': {
      color: '#9ca3af',
      borderColor: '#e5e7eb',
      backgroundColor: '#f9fafb',
      opacity: 1,
    },
  },
  autoTrackHeaderBtnContainedMuted: {
    backgroundColor: '#e5e7eb',
    color: '#9ca3af',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#e5e7eb',
    },
    '&.Mui-disabled': {
      backgroundColor: '#e5e7eb',
      color: '#9ca3af',
      opacity: 1,
    },
  },
}));

const COLORS = ['#f9a620', '#ffd449', '#aab03c', '#548c2f', '#000000'];

const calculateEngagementRate = (likes, comments, shares, views) => {
  likes = likes !== null ? likes : 0;
  comments = comments !== null ? comments : 0;
  shares = shares !== null ? shares : 0;
  if (views === 0 || views === null) {
    return 0;
  }
  const engagementRate = ((likes + comments + shares) / views) * 100;
  return parseFloat(engagementRate.toFixed(2));
};

export default function Detail() {
  const classes = useStyles();
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  
  // Check if component is loaded in an iframe
  const isInIframe = window.self !== window.top;

  // Tab and view state
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);

  // Metric filter state
  const [categoryBreakdownMetric, setCategoryBreakdownMetric] = useState('Posts');
  const [influencerCategoryMetric, setInfluencerCategoryMetric] = useState('creators');

  // Brand and data state
  const [competitorBrands, setCompetitorBrands] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null); // Single brand for Influencers and Posts tabs
  const [summary, setSummary] = useState({});
  const [influencersData, setInfluencersData] = useState([]);
  const [postsData, setPostsData] = useState([]);
  const [brandInsights, setBrandInsights] = useState(null); // Data from competitor-insights API
  const [isSingleBrandView, setIsSingleBrandView] = useState(false);
  const [, setBrandName] = useState('');
  const [reportSnack, setReportSnack] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [reportGenerating, setReportGenerating] = useState(false);
  const [intelEmailSending, setIntelEmailSending] = useState(false);
  const [intelReportRemoteStatus, setIntelReportRemoteStatus] = useState(null);
  /** Last status seen by remote poll (for transition → completed toast). */
  const intelPollPrevStatusRef = useRef(null);
  /** After a successful on-device generate, skip one duplicate "completed" toast from the poll worker. */
  const suppressIntelRemoteCompletedToastRef = useRef(false);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [, setIsStaff] = useState(false);

  // Email + staff flag (staff see multibrand CSV download)
  useEffect(() => {
    getCurrentUser().then((data) => {
      setIsStaff(!!data.data?.is_staff);
      const email = data.data?.email || data.data?.user?.email || '';
      setCurrentUserEmail(typeof email === 'string' ? email.trim() : '');
    });
  }, []);

  // Auto-Track state
  const [combineId, setCombineId] = useState(null);
  const [reportHistoryOpen, setReportHistoryOpen] = useState(false);
  const [autoTrackTab, setAutoTrackTab] = useState('history');
  const [autoTrackEnabled, setAutoTrackEnabled] = useState(false);
  const [atFrequency, setAtFrequency] = useState('monthly');
  const [atDay, setAtDay] = useState(1);
  const [atRecipients, setAtRecipients] = useState([]);
  const [atEmailInput, setAtEmailInput] = useState('');
  const [atPdf, setAtPdf] = useState(true);
  const [atDashboardLink, setAtDashboardLink] = useState(true);
  const [, setAtLastSentAt] = useState(null);
  const [reportHistory, setReportHistory] = useState([]);
  const [, setAutoTrackLoading] = useState(false);
  const [autoTrackSaving, setAutoTrackSaving] = useState(false);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [runNowLoading, setRunNowLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [runNowAnchorEl, setRunNowAnchorEl] = useState(null);
  const [intelReportAnchorEl, setIntelReportAnchorEl] = useState(null);
  const [competitorAudienceDemographics, setCompetitorAudienceDemographics] = useState(null);

  const autoTrackRunInProgress = reportHistory.some((h) => h.status === 'in_progress');

  // Calculate metrics directly from summary and selectedBrands (for multi-brand view)
  const calculatedMetrics = useMemo(() => {
    if (isSingleBrandView || selectedBrands.length === 0 || Object.keys(summary).length === 0) {
      const emptyMetrics = {
        totalPosts: 0,
        avgEngagementRate: 0,
        totalInfluencers: 0,
        totalEngagements: 0,
        reelViews: 0,
      };
      console.log('Calculated Metrics (empty):', {
        isSingleBrandView,
        selectedBrandsLength: selectedBrands.length,
        summaryKeys: Object.keys(summary).length,
      });
      return emptyMetrics;
    }

    let totalPosts = 0;
    let totalEngagements = 0;
    let totalInfluencers = 0;
    let totalViews = 0;
    let totalEngagementRate = 0;
    let brandsWithData = 0;

    console.log('Calculating metrics for brands:', selectedBrands);
    console.log('Available summary keys:', Object.keys(summary));

    selectedBrands.forEach((brandTitle) => {
      const brandData = summary[brandTitle];
      console.log(`Brand: ${brandTitle}, Data exists: ${!!brandData}`, brandData);
      if (brandData) {
        const posts = brandData.count || 0;
        const engagements = (brandData.likes_count || 0) + (brandData.comments_count || 0);
        const influencers = brandData.creators || 0;
        const views = brandData.view_count || 0;
        const engagementRate = brandData.engagement__rate || 0;

        console.log(
          `  - Posts: ${posts}, Engagements: ${engagements}, Influencers: ${influencers}, Views: ${views}`,
        );

        totalPosts += posts;
        totalEngagements += engagements;
        totalInfluencers += influencers;
        totalViews += views;
        totalEngagementRate += engagementRate;

        if (posts > 0 || engagements > 0 || influencers > 0 || views > 0) {
          brandsWithData++;
        }
      } else {
        console.log(`  - No data found for brand: ${brandTitle}`);
      }
    });

    const avgEngagementRate = brandsWithData > 0 ? totalEngagementRate / brandsWithData : 0;

    const result = {
      totalPosts,
      avgEngagementRate,
      totalInfluencers,
      totalEngagements,
      reelViews: totalViews,
    };

    console.log('Calculated Metrics Result:', result);

    return result;
  }, [selectedBrands, summary, isSingleBrandView]);

  // Metrics state (for single brand view or initial state)
  const [metricsData, setMetricsData] = useState({
    totalPosts: 0,
    avgEngagementRate: 0,
    totalInfluencers: 0,
    totalEngagements: 0,
    reelViews: 0,
  });

  // Get date range and platform from URL params
  const query = new URLSearchParams(location.search);
  const start_date = query.get('start_date') || null;
  const end_date = query.get('end_date') || null;
  const platform = query.get('platform') || 'instagram'; // Default to 'instagram', can be 'yt' or 'youtube' for YouTube

  // Date filter state - temporary state for date pickers (not applied until Apply is clicked)
  const [tempStartDate, setTempStartDate] = useState(() => {
    return start_date ? new Date(start_date) : null;
  });
  const [tempEndDate, setTempEndDate] = useState(() => {
    return end_date ? new Date(end_date) : null;
  });

  // Effective dates used for API calls — fall back to brand dates when URL has none
  const [resolvedStartDate, setResolvedStartDate] = useState(start_date);
  const [resolvedEndDate, setResolvedEndDate] = useState(end_date);

  // Update temporary date state when URL params change (when dates are applied)
  useEffect(() => {
    setTempStartDate(start_date ? new Date(start_date) : null);
    setTempEndDate(end_date ? new Date(end_date) : null);
  }, [start_date, end_date]);

  // Sync URL with parent window when in iframe
  useEffect(() => {
    if (isInIframe) {
      const syncUrlWithParent = () => {
        const currentPath = history.location.pathname;
        const search = history.location.search;
        window.parent.postMessage({
          type: 'COMPETITOR_REPORT_NAVIGATE',
          path: `/planner${currentPath}${search}`
        }, '*');
      };

      // Sync on mount
      syncUrlWithParent();

      // Listen for history changes
      const unlisten = history.listen((location) => {
        syncUrlWithParent();
      });

      // Listen for browser back/forward buttons
      window.addEventListener('popstate', syncUrlWithParent);

      return () => {
        unlisten();
        window.removeEventListener('popstate', syncUrlWithParent);
      };
    }
  }, [isInIframe, history]);

  // Handler to update temporary date state (doesn't update URL)
  const handleDateChange = useCallback((dateType, date) => {
    if (dateType === 'start') {
      setTempStartDate(date);
    } else {
      setTempEndDate(date);
    }
  }, []);

  // Handler to apply dates to URL params and trigger refresh
  const handleApplyDates = useCallback(() => {
    const currentUrl = new URL(window.top.location.href);

    if (tempStartDate) {
      currentUrl.searchParams.set('start_date', moment(tempStartDate).format('YYYY-MM-DD'));
    } else {
      currentUrl.searchParams.delete('start_date');
    }

    if (tempEndDate) {
      currentUrl.searchParams.set('end_date', moment(tempEndDate).format('YYYY-MM-DD'));
    } else {
      currentUrl.searchParams.delete('end_date');
    }

    window.top.location.href = currentUrl.toString();
  }, [tempStartDate, tempEndDate]);

  // Handler to clear dates
  const handleClearDates = useCallback(() => {
    setTempStartDate(null);
    setTempEndDate(null);
    const currentUrl = new URL(window.top.location.href);
    currentUrl.searchParams.delete('start_date');
    currentUrl.searchParams.delete('end_date');
    window.top.location.href = currentUrl.toString();
  }, []);

  const calculateMetrics = useCallback((summary_dict, brands) => {
    let totalPosts = 0;
    let totalEngagements = 0;
    let totalInfluencers = 0;
    let totalViews = 0;
    let totalEngagementRate = 0;
    let brandsWithData = 0;

    // Handle both brand objects (with .title) and brand title strings
    const brandTitles = brands.map((brand) => (typeof brand === 'string' ? brand : brand.title));

    brandTitles.forEach((brandTitle) => {
      const brandData = summary_dict[brandTitle];
      if (brandData) {
        const posts = brandData.count || 0;
        const engagements = (brandData.likes_count || 0) + (brandData.comments_count || 0);
        const influencers = brandData.creators || 0;
        const views = brandData.view_count || 0;
        const engagementRate = brandData.engagement__rate || 0;

        totalPosts += posts;
        totalEngagements += engagements;
        totalInfluencers += influencers;
        totalViews += views;
        totalEngagementRate += engagementRate;

        if (posts > 0 || engagements > 0 || influencers > 0 || views > 0) {
          brandsWithData++;
        }
      }
    });

    // Calculate average engagement rate only from brands that have data
    const avgEngagementRate = brandsWithData > 0 ? totalEngagementRate / brandsWithData : 0;

    setMetricsData({
      totalPosts,
      avgEngagementRate,
      totalInfluencers,
      totalEngagements,
      reelViews: totalViews,
    });
  }, []);

  const aggregateInfluencersAndPosts = useCallback((summary_dict, brands) => {
    const allInfluencers = [];
    const allPosts = [];

    // Handle both brand objects (with .title) and brand title strings
    const brandTitles = brands.map((brand) => (typeof brand === 'string' ? brand : brand.title));

    brandTitles.forEach((brandTitle) => {
      const brandData = summary_dict[brandTitle];
      if (brandData) {
        if (brandData.creators_profile) {
          brandData.creators_profile.forEach((creator) => {
            allInfluencers.push({
              ...creator,
              brand: brandTitle,
            });
          });
        }
        if (brandData.posts) {
          brandData.posts.forEach((post) => {
            allPosts.push({
              ...post,
              brand: brandTitle,
            });
          });
        }
      }
    });

    setInfluencersData(allInfluencers);
    setPostsData(allPosts);
  }, []);

  const loadMultiBrandData = useCallback(async () => {
    let effectiveStartDate = start_date;
    let effectiveEndDate = end_date;

    const loadSummaryData = async (results, index, summary_dict, totalViews, totalEng) => {
      if (index < results.length) {
        // Determine which platform to fetch based on platform parameter
        const isYouTube = platform === 'yt' || platform === 'youtube';

        let brandName = results[index].title;
        // Use platform-specific handle - for YouTube use yt_handle, for Instagram use ig_handle
        const brandHandle = isYouTube
          ? results[index].yt_handle || results[index].handle
          : results[index].ig_handle || results[index].handle;

        console.log(`[loadSummaryData] Processing brand ${index + 1}/${results.length}:`, {
          brandName,
          isYouTube,
          brandHandle,
          yt_handle: results[index].yt_handle,
          ig_handle: results[index].ig_handle,
        });

        // Skip "Test - blank YT" to avoid unnecessary API calls
        if (brandHandle === 'Test - blank YT' || brandName === 'Test - blank YT') {
          // Skip to next brand
          if (index + 1 < results.length) {
            await loadSummaryData(results, index + 1, summary_dict, totalViews, totalEng);
          } else {
            setSummary(summary_dict);
            calculateMetrics(summary_dict, results);
            aggregateInfluencersAndPosts(summary_dict, results);
          }
          return;
        }

        try {
          if (!summary_dict[brandName]) {
            summary_dict[brandName] = {};
          }

          // Fetch Instagram data (only if platform is not YouTube)
          if (!isYouTube) {
            try {
              const resp = await creatorTypesWithNormalTypeFetch(
                results[index].ig_handle,
                effectiveStartDate || '',
                effectiveEndDate || '',
                '',
                '',
                results[index].excluded_handles,
              );

              if (resp && resp.all_creators) {
                summary_dict[brandName].creators = resp.all_creators.creators || 0;
                summary_dict[brandName].followers = resp.all_creators.followers || 0;
                summary_dict[brandName].likes_count = resp.all_creators.likes_count || 0;
                summary_dict[brandName].comments_count = resp.all_creators.comments_count || 0;
                summary_dict[brandName].count = resp.all_creators.count || 0;
                summary_dict[brandName].engagement__rate = calculateEngagementRate(
                  resp.all_creators.likes_count || 0,
                  resp.all_creators.comments_count || 0,
                  0,
                  resp.all_creators.view_count || 0,
                );
                summary_dict[brandName].view_count = resp.all_creators.view_count || 0;
                summary_dict[brandName].fair_price = resp.all_creators.fair_price || 0;
                summary_dict[brandName]['instagram'] = {
                  creators: resp.all_creators.creators || 0,
                  followers: resp.all_creators.followers || 0,
                  likes_count: resp.all_creators.likes_count || 0,
                  comments_count: resp.all_creators.comments_count || 0,
                  count: resp.all_creators.count || 0,
                  engagement__rate: resp.all_creators.engagement__rate || 0,
                  view_count: resp.all_creators.view_count || 0,
                  fair_price: resp.all_creators.fair_price || 0,
                };
              } else {
                // Initialize with zero values if no data returned
                summary_dict[brandName].creators = summary_dict[brandName].creators || 0;
                summary_dict[brandName].followers = summary_dict[brandName].followers || 0;
                summary_dict[brandName].likes_count = summary_dict[brandName].likes_count || 0;
                summary_dict[brandName].comments_count =
                  summary_dict[brandName].comments_count || 0;
                summary_dict[brandName].count = summary_dict[brandName].count || 0;
                summary_dict[brandName].view_count = summary_dict[brandName].view_count || 0;
                summary_dict[brandName].fair_price = summary_dict[brandName].fair_price || 0;
                summary_dict[brandName].engagement__rate =
                  summary_dict[brandName].engagement__rate || 0;
              }

              // Process creator types
              if (resp && resp.creator_type) {
                resp.creator_type.forEach((each) => {
                  const type = each.creator_type;
                  if (!summary_dict[brandName][type]) {
                    summary_dict[brandName][type] = { ...each };
                  } else {
                    summary_dict[brandName][type].creators += each.creators || 0;
                    summary_dict[brandName][type].followers += each.followers || 0;
                    summary_dict[brandName][type].likes_count += each.likes_count || 0;
                    summary_dict[brandName][type].comments_count += each.comments_count || 0;
                    summary_dict[brandName][type].count += each.count || 0;
                    summary_dict[brandName][type].engagement__rate += each.engagement__rate || 0;
                    summary_dict[brandName][type].view_count += each.view_count || 0;
                    summary_dict[brandName][type].fair_price += each.fair_price || 0;
                  }
                });
              }
            } catch (error) {
              console.error(`Error fetching Instagram data for ${brandName}:`, error);
              // Initialize with zero values on error
              summary_dict[brandName].creators = summary_dict[brandName].creators || 0;
              summary_dict[brandName].followers = summary_dict[brandName].followers || 0;
              summary_dict[brandName].likes_count = summary_dict[brandName].likes_count || 0;
              summary_dict[brandName].comments_count = summary_dict[brandName].comments_count || 0;
              summary_dict[brandName].count = summary_dict[brandName].count || 0;
              summary_dict[brandName].view_count = summary_dict[brandName].view_count || 0;
              summary_dict[brandName].fair_price = summary_dict[brandName].fair_price || 0;
              summary_dict[brandName].engagement__rate =
                summary_dict[brandName].engagement__rate || 0;
            }
          }

          // Fetch YouTube data (only if platform is YouTube)
          if (isYouTube) {
            const ytHandle = results[index].yt_handle || '';
            if (ytHandle && !ytHandle.toLowerCase().includes('test')) {
              try {
                const resp_yt = await creatorTypesYoutubeWithNormalTypeFetch(
                  ytHandle,
                  effectiveStartDate || '',
                  effectiveEndDate || '',
                  '',
                  '',
                  results[index].excluded_channel_ids,
                );

                if (resp_yt && resp_yt.all_creators) {
                  summary_dict[brandName].creators = resp_yt.all_creators.creators || 0;
                  summary_dict[brandName].followers = resp_yt.all_creators.followers || 0;
                  summary_dict[brandName].likes_count = resp_yt.all_creators.likes_count || 0;
                  summary_dict[brandName].comments_count = resp_yt.all_creators.comments_count || 0;
                  summary_dict[brandName].count = resp_yt.all_creators.count || 0;
                  summary_dict[brandName].view_count = resp_yt.all_creators.view_count || 0;
                  summary_dict[brandName].fair_price = resp_yt.all_creators.fair_price || 0;
                  summary_dict[brandName].engagement__rate = calculateEngagementRate(
                    resp_yt.all_creators.likes_count || 0,
                    resp_yt.all_creators.comments_count || 0,
                    0,
                    resp_yt.all_creators.view_count || 0,
                  );
                  summary_dict[brandName]['youtube'] = {
                    creators: resp_yt.all_creators.creators || 0,
                    followers: resp_yt.all_creators.followers || 0,
                    likes_count: resp_yt.all_creators.likes_count || 0,
                    comments_count: resp_yt.all_creators.comments_count || 0,
                    count: resp_yt.all_creators.count || 0,
                    engagement__rate: resp_yt.all_creators.engagement__rate || 0,
                    view_count: resp_yt.all_creators.view_count || 0,
                    fair_price: resp_yt.all_creators.fair_price || 0,
                  };
                } else {
                  // Initialize with zero values if no data returned
                  summary_dict[brandName].creators = summary_dict[brandName].creators || 0;
                  summary_dict[brandName].followers = summary_dict[brandName].followers || 0;
                  summary_dict[brandName].likes_count = summary_dict[brandName].likes_count || 0;
                  summary_dict[brandName].comments_count =
                    summary_dict[brandName].comments_count || 0;
                  summary_dict[brandName].count = summary_dict[brandName].count || 0;
                  summary_dict[brandName].view_count = summary_dict[brandName].view_count || 0;
                  summary_dict[brandName].fair_price = summary_dict[brandName].fair_price || 0;
                  summary_dict[brandName].engagement__rate =
                    summary_dict[brandName].engagement__rate || 0;
                }

                // Process YouTube creator types
                if (resp_yt && resp_yt.creator_type) {
                  resp_yt.creator_type.forEach((each) => {
                    const type = each.creator_type;
                    if (!summary_dict[brandName][type]) {
                      summary_dict[brandName][type] = { ...each };
                    } else {
                      summary_dict[brandName][type].creators += each.creators || 0;
                      summary_dict[brandName][type].followers += each.followers || 0;
                      summary_dict[brandName][type].likes_count += each.likes_count || 0;
                      summary_dict[brandName][type].comments_count += each.comments_count || 0;
                      summary_dict[brandName][type].count += each.count || 0;
                      summary_dict[brandName][type].engagement__rate += each.engagement__rate || 0;
                      summary_dict[brandName][type].view_count += each.view_count || 0;
                      summary_dict[brandName][type].fair_price += each.fair_price || 0;
                    }
                  });
                }
              } catch (error) {
                console.error(`Error fetching YouTube data for ${brandName}:`, error);
                // Initialize with zero values on error
                summary_dict[brandName].creators = summary_dict[brandName].creators || 0;
                summary_dict[brandName].followers = summary_dict[brandName].followers || 0;
                summary_dict[brandName].likes_count = summary_dict[brandName].likes_count || 0;
                summary_dict[brandName].comments_count =
                  summary_dict[brandName].comments_count || 0;
                summary_dict[brandName].count = summary_dict[brandName].count || 0;
                summary_dict[brandName].view_count = summary_dict[brandName].view_count || 0;
                summary_dict[brandName].fair_price = summary_dict[brandName].fair_price || 0;
                summary_dict[brandName].engagement__rate =
                  summary_dict[brandName].engagement__rate || 0;
              }
            } else {
              // No YouTube handle - initialize with zero values
              summary_dict[brandName].creators = summary_dict[brandName].creators || 0;
              summary_dict[brandName].followers = summary_dict[brandName].followers || 0;
              summary_dict[brandName].likes_count = summary_dict[brandName].likes_count || 0;
              summary_dict[brandName].comments_count = summary_dict[brandName].comments_count || 0;
              summary_dict[brandName].count = summary_dict[brandName].count || 0;
              summary_dict[brandName].view_count = summary_dict[brandName].view_count || 0;
              summary_dict[brandName].fair_price = summary_dict[brandName].fair_price || 0;
              summary_dict[brandName].engagement__rate =
                summary_dict[brandName].engagement__rate || 0;
            }
          }

          // // Fetch creators
          // const creators = await creatorsFetch(
          //   results[index].ig_handle,
          //   start_date || '',
          //   end_date || '',
          //   '',
          //   false,
          //   results[0]?.ig_handle,
          //   results[index].excluded_handles
          // );
          // // Fetch YouTube creators - Skip if handle contains "test"
          // let ytCreators = null;
          // const ytHandleForCreators = results[index].yt_handle || '';
          // if (ytHandleForCreators && !ytHandleForCreators.toLowerCase().includes('test')) {
          //   try {
          //     ytCreators = await ytCreatorsFetch(
          //       ytHandleForCreators,
          //       start_date || '',
          //       end_date || '',
          //   '',
          //   false,
          //   results[0]?.yt_handle,
          //   results[index].excluded_channel_ids
          // );
          //   } catch (error) {
          //     console.error(`Error fetching YouTube creators for ${brandName}:`, error);
          //   }
          // }

          // let creatorsList = creators?.results || [];
          // let ytCreatorsList = (ytCreators && ytCreators.results) ? ytCreators.results : [];

          // // Filter excluded handles
          // const excludedList = (results[index].excluded_handles && results[index].excluded_handles.split(',')) || [];
          // if (excludedList.length > 0) {
          //   creatorsList = creatorsList.filter((each_creator) => {
          //     return excludedList.indexOf(each_creator.handle.replace('@', '')) === -1;
          //   });
          // }

          // const excludedYtList = (results[index].excluded_channel_ids && results[index].excluded_channel_ids.split(',')) || [];
          // if (excludedYtList.length > 0) {
          //   ytCreatorsList = ytCreatorsList.filter((each_creator) => {
          //     return excludedYtList.indexOf(each_creator.channel_id) === -1;
          //   });
          // }

          // // Combine all creators without slicing
          // let combinedCreators = [...creatorsList, ...ytCreatorsList];
          // summary_dict[brandName]['creators_profile'] = combinedCreators;

          // Continue with next brand
          if (index + 1 < results.length) {
            await loadSummaryData(results, index + 1, summary_dict, totalViews, totalEng);
          } else {
            // All brands processed, update state
            setSummary(summary_dict);
            calculateMetrics(summary_dict, results);
            aggregateInfluencersAndPosts(summary_dict, results);
          }
        } catch (error) {
          console.error(`Error loading data for brand ${brandName}:`, error);
          // Continue with next brand even if this one fails
          if (index + 1 < results.length) {
            await loadSummaryData(results, index + 1, summary_dict, totalViews, totalEng);
          } else {
            setSummary(summary_dict);
            calculateMetrics(summary_dict, results);
            aggregateInfluencersAndPosts(summary_dict, results);
          }
        }
      }
    };

    try {
      const query = new URLSearchParams(location.search);
      const report_title = query.get('report_title');
      const ids = query.get('ids');

      // UUIDs contain hyphens; numeric ids are legacy brand_id lookups.
      const isGroupId = id && id.includes('-');
      const { results } = isGroupId
        ? await getCombinedBrandListCompetitor(null, null, null, id)
        : await getCombinedBrandListCompetitor(id, report_title, ids);

      if (results && results.length > 0) {
        setCombineId(results[0].id);
        if (!effectiveStartDate) {
          effectiveStartDate = results[0].start_date || '';
          if (effectiveStartDate) {
            setTempStartDate(new Date(effectiveStartDate));
            setResolvedStartDate(effectiveStartDate);
          }
        }
        if (!effectiveEndDate) {
          effectiveEndDate = results[0].end_date || '';
          if (effectiveEndDate) {
            setTempEndDate(new Date(effectiveEndDate));
            setResolvedEndDate(effectiveEndDate);
          }
        }
      }

      // Determine which platform to filter for
      const isYouTube = platform === 'yt' || platform === 'youtube';

      console.log(`[loadMultiBrandData] Platform: ${platform}, isYouTube: ${isYouTube}`);
      console.log(`[loadMultiBrandData] Total brands from API:`, results?.length || 0);
      if (results && results.length > 0) {
        console.log(`[loadMultiBrandData] Sample brand:`, {
          title: results[0].title,
          ig_handle: results[0].ig_handle,
          yt_handle: results[0].yt_handle,
        });
      }

      // Filter brands based on platform - only include brands with the appropriate handle
      const filteredResults = results.filter((brand) => {
        // For YouTube platform, only check yt_handle
        if (isYouTube) {
          // Skip if no yt_handle or if it's "Test - blank YT"
          if (
            !brand.yt_handle ||
            brand.yt_handle === 'Test - blank YT' ||
            brand.yt_handle.toLowerCase().includes('test')
          ) {
            console.log(
              `[loadMultiBrandData] Filtering out brand "${
                brand.title || brand.handle
              }": no valid yt_handle (${brand.yt_handle})`,
            );
            return false;
          }
          return true;
        }

        // For Instagram platform, only check ig_handle
        // Skip if no ig_handle or if it's "Test - blank YT"
        if (
          !brand.ig_handle ||
          brand.ig_handle.trim() === '' ||
          brand.ig_handle === 'Test - blank YT'
        ) {
          console.log(
            `[loadMultiBrandData] Filtering out brand "${
              brand.title || brand.handle
            }": no valid ig_handle (${brand.ig_handle})`,
          );
          return false;
        }
        return true;
      });

      console.log(`[loadMultiBrandData] Filtered brands count:`, filteredResults.length);
      console.log(
        `[loadMultiBrandData] Filtered brands:`,
        filteredResults.map((b) => ({
          title: b.title,
          ig_handle: b.ig_handle,
          yt_handle: b.yt_handle,
        })),
      );

      const brandsList = filteredResults.slice(0, 6);
      setCompetitorBrands(brandsList);
      setSelectedBrands(brandsList.map((b) => b.title));
      setSelectedBrand(brandsList[0]?.title || null); // Set initial single brand selection

      console.log(
        `[loadMultiBrandData] Setting selectedBrands:`,
        brandsList.map((b) => b.title),
      );

      await loadSummaryData(brandsList, 0, {}, 0, 0);
    } catch (error) {
      console.error('Error loading multi-brand data:', error);
    }
  }, [id, location.search, start_date, end_date, platform, calculateMetrics, aggregateInfluencersAndPosts]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      // Check if id is a brand_id (numeric) - single brand view
      const isBrandId = !isNaN(id) && id.length > 0;
      const isYouTube = platform === 'yt' || platform === 'youtube';

      if (isBrandId && !isYouTube) {
        try {
          const insightsData = await getCompetitorInsights(id, start_date, end_date, platform);
          if (insightsData) {
            setBrandInsights(insightsData);
            setBrandName(insightsData.brand_name || 'Brand');

            // If insights data has competitor brands, treat as multi-brand view
            if (
              insightsData.competitors &&
              Array.isArray(insightsData.competitors) &&
              insightsData.competitors.length > 0
            ) {
              // Set as multi-brand view so calculatedMetrics will work
              setIsSingleBrandView(false);
              const brands = insightsData.competitors
                .filter((comp) => {
                  const handle = comp.handle || comp.ig_handle || comp.name || comp.brand_name;
                  return handle && handle !== 'Test - blank YT';
                })
                .map((comp) => ({
                  title: comp.brand_name || comp.name,
                  ig_handle: comp.ig_handle || comp.handle,
                  yt_handle: comp.yt_handle || comp.youtube_handle,
                  excluded_handles: comp.excluded_handles,
                  excluded_channel_ids: comp.excluded_channel_ids,
                }));
              setCompetitorBrands(brands);
              setSelectedBrands(brands.map((b) => b.title));
              // Load summary data - this will populate the summary state
              await loadMultiBrandData();
            } else {
              // True single brand view - no competitors
              setIsSingleBrandView(true);
              // Extract metrics from insights data
              if (insightsData.metrics) {
                setMetricsData({
                  totalPosts: insightsData.metrics.total_posts || 0,
                  avgEngagementRate: insightsData.metrics.avg_engagement_rate || 0,
                  totalInfluencers: insightsData.metrics.total_influencers || 0,
                  totalEngagements: insightsData.metrics.total_engagements || 0,
                  reelViews: insightsData.metrics.total_views || 0,
                });
              }
            }
          }
        } catch (error) {
          // Only log error if it's not a non-JSON response (expected for unsupported platforms)
          if (!error.message || !error.message.includes('non-JSON response')) {
            console.error('Error loading competitor insights:', error);
          }
          // Fall back to existing method if API fails
          setIsSingleBrandView(false);
          await loadMultiBrandData();
        }
      } else {
        // Multi-brand view or YouTube platform - use existing logic
        setIsSingleBrandView(false);
        await loadMultiBrandData();
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [id, start_date, end_date, platform, loadMultiBrandData]);

  useEffect(() => {
    loadData();
  }, [loadData, location.search]);

  // Fetch auto-track settings when combineId is known
  useEffect(() => {
    if (!combineId) return;
    setAutoTrackLoading(true);
    getAutoTrackSettings(combineId)
      .then((data) => {
        if (!data) return;
        setAutoTrackEnabled(data.auto_track_enabled ?? false);
        setAtFrequency(data.auto_track_frequency || 'monthly');
        setAtDay(data.auto_track_day_of_month ?? 1);
        setAtRecipients(data.auto_track_recipients || []);
        setAtPdf(data.auto_track_pdf_attachment ?? true);
        setAtDashboardLink(data.auto_track_dashboard_link ?? true);
        setAtLastSentAt(data.auto_track_last_sent_at || null);
      })
      .catch(console.error)
      .finally(() => setAutoTrackLoading(false));
  }, [combineId]);

  const fetchAutoTrackHistory = useCallback((options = {}) => {
    const { silent = false } = options;
    if (!combineId) return Promise.resolve();
    if (!silent) setHistoryLoading(true);
    return getAutoTrackHistory(combineId)
      .then((data) => setReportHistory(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => {
        if (!silent) setHistoryLoading(false);
      });
  }, [combineId]);

  // Fetch auto-track history on initial load to determine Generate Report vs Email PDF state
  useEffect(() => {
    if (!combineId) return;
    fetchAutoTrackHistory({ silent: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combineId]);

  // Fetch auto-track history when modal opens on history tab
  useEffect(() => {
    if (!reportHistoryOpen || !combineId) return;
    fetchAutoTrackHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportHistoryOpen, combineId]);

  // Poll while starting a run or while a run row is in_progress so Run Now / History stay in sync
  useEffect(() => {
    if (!combineId || (!autoTrackRunInProgress && !runNowLoading)) return;
    const pollMs = 4000;
    const tick = () => fetchAutoTrackHistory({ silent: true });
    const intervalId = setInterval(tick, pollMs);
    return () => clearInterval(intervalId);
  }, [combineId, autoTrackRunInProgress, runNowLoading, fetchAutoTrackHistory]);

  const handlePauseResume = useCallback(() => {
    if (!combineId || pauseLoading) return;
    const next = !autoTrackEnabled;
    setPauseLoading(true);
    pauseAutoTrack(combineId, next)
      .then(() => {
        setAutoTrackEnabled(next);
        setAutoTrackTab('history');
        fetchAutoTrackHistory();
      })
      .catch(console.error)
      .finally(() => setPauseLoading(false));
  }, [combineId, autoTrackEnabled, pauseLoading, fetchAutoTrackHistory]);

  const handleRunNow = useCallback((deviceType = 'desktop') => {
    if (!combineId || runNowLoading) return;
    setRunNowAnchorEl(null);
    setRunNowLoading(true);
    runAutoTrackNow(combineId, deviceType)
      .then(() => fetchAutoTrackHistory())
      .catch(console.error)
      .finally(() => setRunNowLoading(false));
  }, [combineId, runNowLoading, fetchAutoTrackHistory]);


  const handleDownloadData = useCallback(async () => {
    if (downloadLoading) return;
    setDownloadLoading(true);
    try {
      const isYouTube = platform === 'yt' || platform === 'youtube';
      const brandHandles = competitorBrands
        .map((brand) => (isYouTube ? brand.yt_handle : brand.ig_handle))
        .filter(Boolean);
      if (isYouTube) {
        await downloadNewYoutubeData({ brandHandles, startDate: start_date, endDate: end_date, searchKeyword: '' });
      } else {
        await downloadNewInstagramData({ brandHandles, startDate: start_date, endDate: end_date, searchKeyword: '' });
      }
    } catch (error) {
      console.error('Error downloading data:', error);
    } finally {
      setDownloadLoading(false);
    }
  }, [downloadLoading, platform, competitorBrands, start_date, end_date]);

  const handleSaveAutoTrackSettings = useCallback(() => {
    if (!combineId) return;

    // Auto-commit any email still pending in the input
    let finalRecipients = atRecipients;
    if (atEmailInput.trim()) {
      const pending = atEmailInput.trim();
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pending) && !finalRecipients.includes(pending)) {
        finalRecipients = [...finalRecipients, pending];
        setAtRecipients(finalRecipients);
      }
      setAtEmailInput('');
    }

    setAutoTrackSaving(true);
    saveAutoTrackSettings(combineId, {
      auto_track_enabled: autoTrackEnabled,
      auto_track_frequency: atFrequency,
      auto_track_day_of_month: atDay,
      auto_track_recipients: finalRecipients,
      auto_track_pdf_attachment: atPdf,
      auto_track_dashboard_link: atDashboardLink,
    })
      .then(() => {
        setAutoTrackTab('history');
        fetchAutoTrackHistory();
      })
      .catch(console.error)
      .finally(() => setAutoTrackSaving(false));
  }, [combineId, autoTrackEnabled, atFrequency, atDay, atRecipients, atEmailInput, atPdf, atDashboardLink, fetchAutoTrackHistory]);

  // Update metricsData when calculatedMetrics changes (for multi-brand view)
  useEffect(() => {
    if (!isSingleBrandView) {
      console.log('Updating metricsData from calculatedMetrics:', calculatedMetrics);
      setMetricsData(calculatedMetrics);
    }
  }, [calculatedMetrics, isSingleBrandView]);

  // Recalculate metrics when selected brands or summary data changes (for multi-brand view)
  // This is kept for backward compatibility but calculatedMetrics is the primary source
  useEffect(() => {
    // Only recalculate for multi-brand view
    if (isSingleBrandView) {
      return;
    }

    // If we have selected brands and summary data, calculate metrics
    if (selectedBrands.length > 0 && Object.keys(summary).length > 0) {
      // Calculate metrics for all selected brands (even if some don't have data yet)
      // This ensures metrics update immediately when brands are toggled
      calculateMetrics(summary, selectedBrands);
    } else if (selectedBrands.length === 0) {
      // If no brands are selected, reset metrics to zero
      setMetricsData({
        totalPosts: 0,
        avgEngagementRate: 0,
        totalInfluencers: 0,
        totalEngagements: 0,
        reelViews: 0,
      });
    }
  }, [selectedBrands, summary, isSingleBrandView, calculateMetrics]);

  // Navigation items
  const navItems = [
    { value: 0, label: 'Overview', icon: Home },
    { value: 1, label: 'Influencers', icon: Person },
    { value: 2, label: 'Posts', icon: Description },
    { value: 3, label: 'Themes & Hooks', icon: EmojiObjects },
  ];

  // Overview and Themes & Hooks compare brands side by side, so they multi-select;
  // Influencers and Posts drill into one brand at a time.
  const isMultiBrandTab = tabValue === 0 || tabValue === 3;

  // Handlers
  const handleBrandToggle = useCallback(
    (brand) => {
      // Overview and Themes & Hooks tabs: multi-select
      if (tabValue === 0 || tabValue === 3) {
        setSelectedBrands((prev) => {
          // Don't allow deselecting all brands
          if (prev.includes(brand) && prev.length === 1) {
            console.log('Cannot deselect last brand');
            return prev;
          }
          const newBrands = prev.includes(brand)
            ? prev.filter((b) => b !== brand)
            : [...prev, brand];
          console.log('Brand toggle:', { brand, prev, newBrands });
          return newBrands;
        });
      } else {
        // Influencers and Posts tabs: single select
        setSelectedBrand(brand);
      }
    },
    [tabValue],
  );




  // Calculate brand comparison data from API
  const brandComparisonData = useMemo(() => {
    return selectedBrands.map((brand) => {
      const brandData = summary[brand];
      if (!brandData) {
        return {
          brand,
          creatorCount: 0,
          posts: 0,
          views: 0,
          engagement: 0,
        };
      }

      const brandInfluencers = influencersData.filter((inf) => inf.brand === brand);
      const brandPosts = postsData.filter((p) => p.brand === brand);

      return {
        brand,
        creatorCount: brandData.creators || brandInfluencers.length,
        posts: brandData.count || brandPosts.length,
        views: brandData.view_count || 0,
        engagement: (brandData.likes_count || 0) + (brandData.comments_count || 0),
      };
    });
  }, [selectedBrands, summary, influencersData, postsData]);


  // Calculate share of voice from API data
  // Always show brand distribution for all brands using posts from all_creators.count
  const filteredShareOfVoiceData = useMemo(() => {
    // Show brand distribution for all brands
    const totalViews = brandComparisonData.reduce((sum, d) => sum + d.views, 0);
    if (totalViews === 0) return [];

    return brandComparisonData.map((d) => ({
      brand: d.brand,
      value: totalViews > 0 ? ((d.views / totalViews) * 100).toFixed(2) : '0.00',
      views: d.views,
    }));
  }, [brandComparisonData]);

  const filteredInfluencerOverlapData = useMemo(() => {
    // Calculate influencer overlap from API data
    const brandInfluencerMap = {};
    selectedBrands.forEach((brand) => {
      const brandInfluencers = influencersData.filter((inf) => inf.brand === brand);
      brandInfluencerMap[brand] = new Set(
        brandInfluencers.map((inf) => inf.handle || inf.channel_id),
      );
    });

    return selectedBrands.map((brand) => {
      const brandInfluencers = brandInfluencerMap[brand] || new Set();
      let shared = 0;
      let exclusive = brandInfluencers.size;

      selectedBrands.forEach((otherBrand) => {
        if (otherBrand !== brand) {
          const otherInfluencers = brandInfluencerMap[otherBrand] || new Set();
          brandInfluencers.forEach((inf) => {
            if (otherInfluencers.has(inf)) {
              shared++;
              exclusive--;
            }
          });
        }
      });

      return {
        brand,
        exclusive: Math.max(0, exclusive),
        shared: Math.max(0, shared),
      };
    });
  }, [selectedBrands, influencersData]);

  const filteredMarketPositionData = useMemo(() => {
    return selectedBrands.map((brand, index) => {
      const brandData = summary[brand];
      const brandComparison = brandComparisonData.find((d) => d.brand === brand);
      return {
        brand,
        influencerCount: brandData?.creators || 0,
        engagementRate: brandData?.engagement__rate || 0,
        posts: brandComparison?.posts || brandData?.count || 0,
        views: brandComparison?.views || brandData?.view_count || 0,
        engagement:
          brandComparison?.engagement ||
          (brandData?.likes_count || 0) + (brandData?.comments_count || 0),
      };
    });
  }, [selectedBrands, summary, brandComparisonData]);

  const shareOfVoiceChart = {
    options: {
      chart: {
        type: 'donut',
        background: 'transparent',
      },
      labels:
        filteredShareOfVoiceData.length > 0
          ? filteredShareOfVoiceData.map((d) => d.brand)
          : ['No Data'],
      colors:
        filteredShareOfVoiceData.length > 0
          ? filteredShareOfVoiceData.map((d, index) => getBrandColor(index))
          : ['#9ca3af'],
      legend: {
        position: 'bottom',
        labels: { colors: '#9ca3af' },
      },
      dataLabels: {
        enabled: filteredShareOfVoiceData.length > 0,
        formatter: (val) => `${parseFloat(val).toFixed(2)}%`,
        style: { colors: ['#fff'] },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '60%',
          },
        },
      },
      theme: { mode: 'dark' },
      noData: {
        text: 'No data available',
        align: 'center',
        verticalAlign: 'middle',
        style: {
          color: '#9ca3af',
          fontSize: '14px',
        },
      },
    },
    series:
      filteredShareOfVoiceData.length > 0
        ? filteredShareOfVoiceData.map((d) => parseFloat(d.value) || 0)
        : [0],
  };

  const marketPositionChart = {
    options: {
      chart: {
        id: 'market-position-chart',
        type: 'scatter',
        toolbar: { show: false },
        background: 'transparent',
        zoom: { enabled: false },
        animations: {
          enabled: true,
        },
      },
      plotOptions: {
        scatter: {
          markers: {
            size: 8,
            strokeWidth: 2,
            strokeOpacity: 1,
            fillOpacity: 1,
            hover: {
              size: 12,
              sizeOffset: 4,
            },
          },
        },
      },
      xaxis: {
        type: 'numeric',
        title: { text: 'Influencer Count', style: { color: '#9ca3af' } },
        labels: { style: { colors: '#9ca3af' } },
      },
      yaxis: {
        type: 'numeric',
        title: { text: 'Engagement Rate (%)', style: { color: '#9ca3af' } },
        labels: { style: { colors: '#9ca3af' } },
      },
      colors: filteredMarketPositionData.map((d, index) => getBrandColor(index)),
      markers: {
        size: 8,
        strokeWidth: 2,
        strokeOpacity: 1,
        fillOpacity: 1,
        hover: {
          size: 12,
          sizeOffset: 4,
        },
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        custom: function ({ seriesIndex, dataPointIndex, w }) {
          const data = filteredMarketPositionData[seriesIndex];
          if (!data) return '';
          return `
            <div style="padding: 10px; background: #1f2937; border-radius: 4px;">
              <div style="font-weight: 600; margin-bottom: 8px; color: #fff;">${data.brand}</div>
              <div style="font-size: 12px; margin-bottom: 4px; color: #9ca3af;">Influencers: ${formatNumber(
                data.influencerCount,
              )}</div>
              <div style="font-size: 12px; margin-bottom: 4px; color: #9ca3af;">Engagement Rate: ${data.engagementRate.toFixed(
                1,
              )}%</div>
              <div style="font-size: 12px; margin-bottom: 4px; color: #9ca3af;">Posts: ${formatNumber(
                data.posts,
              )}</div>
              <div style="font-size: 12px; margin-bottom: 4px; color: #9ca3af;">Views: ${formatNumber(
                data.views,
              )}</div>
              <div style="font-size: 12px; color: #9ca3af;">Engagement: ${formatNumber(
                data.engagement,
              )}</div>
            </div>
          `;
        },
      },
      grid: {
        borderColor: 'rgba(255, 255, 255, 0.1)',
        strokeDashArray: 3,
      },
      theme: { mode: 'dark' },
      legend: {
        show: true,
        position: 'bottom',
        labels: { colors: '#9ca3af' },
      },
    },
    series: filteredMarketPositionData.map((d, index) => {
      const influencerCount = Number(d.influencerCount) || 0;
      const engagementRate = Number(d.engagementRate) || 0;
      return {
        name: d.brand,
        data: [
          {
            x: influencerCount,
            y: engagementRate,
          },
        ],
      };
    }),
  };

  // Get platform display name
  const platformDisplayName = useMemo(() => {
    const isYouTube = platform === 'yt' || platform === 'youtube';
    return isYouTube ? 'YouTube' : 'Instagram';
  }, [platform]);

  // Format metrics for dashboard cards
  const formatMetricValue = useCallback((value, type = 'number') => {
    if (type === 'percentage') {
      return formatPercentage(value);
    }
    if (type === 'large') {
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M`;
      }
      if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}K`;
      }
    }
    return value.toLocaleString();
  }, []);

  // Report title and subtitle


  const combineIdsFromUrl = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('ids');
    if (!raw) return [];
    return raw
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n));
  }, [location.search]);

  const reportTitleForApi = useMemo(() => {
    const t = new URLSearchParams(location.search).get('report_title');
    return t && t.trim() ? t.trim() : 'Competitor Analysis';
  }, [location.search]);

  const combineIdsForIntelligenceReport = useMemo(() => {
    const fromSelection = competitorBrands
      .filter((b) => selectedBrands.includes(b.title))
      .map((b) => b.id)
      .filter((x) => x != null && !Number.isNaN(Number(x)))
      .map((n) => Number(n));
    if (fromSelection.length > 0) return fromSelection;
    return combineIdsFromUrl;
  }, [competitorBrands, selectedBrands, combineIdsFromUrl]);

  const combineIdsKey = useMemo(
    () => combineIdsForIntelligenceReport.join(','),
    [combineIdsForIntelligenceReport],
  );

  useEffect(() => {
    intelPollPrevStatusRef.current = null;
  }, [combineIdsKey]);

  /** While local generate runs we pause remote polling (handled in effect deps). */
  useEffect(() => {
    if (reportGenerating) {
      return undefined;
    }
    let cancelled = false;
    let timeoutId;

    const poll = async () => {
      if (cancelled) return;
      const ids = combineIdsForIntelligenceReport;
      if (!ids.length) {
        setIntelReportRemoteStatus(null);
        return;
      }
      try {
        const data = await getIntelligenceReportStatus(ids);
        if (cancelled) return;
        const st = data?.status != null ? String(data.status) : null;
        const stLower = (st || '').toLowerCase();
        const prev = intelPollPrevStatusRef.current;
        const prevLower = prev != null ? String(prev).toLowerCase() : '';
        const prevWasBusy = ['pending', 'in_progress', 'processing'].includes(prevLower);
        const isCompleted = ['completed', 'complete', 'success', 'done', 'ready'].includes(stLower);

        intelPollPrevStatusRef.current = st;
        setIntelReportRemoteStatus(st);

        if (prevWasBusy && isCompleted) {
          if (suppressIntelRemoteCompletedToastRef.current) {
            suppressIntelRemoteCompletedToastRef.current = false;
          } else {
            setReportSnack({
              open: true,
              message: 'Report generated. It will be sent to your email shortly.',
              severity: 'success',
            });
          }
        }

        const busy = ['pending', 'in_progress', 'processing'].includes(stLower);
        if (busy) {
          timeoutId = window.setTimeout(poll, 2500);
        }
      } catch (_e) {
        if (!cancelled) setIntelReportRemoteStatus(null);
      }
    };

    poll();
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [combineIdsKey, combineIdsForIntelligenceReport, reportGenerating]);

  useEffect(() => {
    if (tabValue !== 0 || competitorBrands.length === 0) return;
    const isYouTube = platform === 'yt' || platform === 'youtube';
    const handles = competitorBrands
      .map((b) => (isYouTube ? b.yt_handle : b.ig_handle))
      .filter(Boolean);
    if (handles.length === 0) return;
    setCompetitorAudienceDemographics(null);
    getCompetitorAudienceDemographics({ brandHandles: handles, platform: isYouTube ? 'youtube' : 'instagram' })
      .then((data) => setCompetitorAudienceDemographics(data))
      .catch(() => {});
  }, [competitorBrands, platform, tabValue]);

  // Build per-brand audience demographics comparison charts (grouped bars) +
  // auto-generated key insights. Each brand keeps its getBrandColor(index),
  // matching the other Overview comparison charts.
  const demographicsComparison = useMemo(() => {
    const isYouTube = platform === 'yt' || platform === 'youtube';

    const byHandle = {};
    (competitorAudienceDemographics?.brands || []).forEach((b) => {
      byHandle[(b.handle || '').trim().toLowerCase()] = b;
    });

    const brandData = selectedBrands
      .map((title, index) => {
        const brandObj = competitorBrands.find((b) => b.title === title);
        const handle = brandObj ? (isYouTube ? brandObj.yt_handle : brandObj.ig_handle) : null;
        const demo = handle ? byHandle[(handle || '').trim().toLowerCase()] : null;
        return { title, color: getBrandColor(index), demo };
      })
      .filter((d) => d.demo);

    if (brandData.length === 0) {
      return { hasData: false };
    }

    const colors = brandData.map((d) => d.color);

    // ---- Age ----
    const ageOrder = (bucket) => {
      const m = String(bucket).match(/\d+/);
      return m ? parseInt(m[0], 10) : 999;
    };
    const ageCats = Array.from(
      new Set(brandData.flatMap((d) => (d.demo.age || []).map((a) => a.bucket))),
    ).sort((a, b) => ageOrder(a) - ageOrder(b));
    const ageSeries = brandData.map((d) => ({
      name: d.title,
      data: ageCats.map((c) => {
        const hit = (d.demo.age || []).find((a) => a.bucket === c);
        return hit ? hit.pct : 0;
      }),
    }));

    // ---- Gender ----
    const genderPref = ['Male', 'Female'];
    const genderRaw = Array.from(
      new Set(brandData.flatMap((d) => (d.demo.gender || []).map((g) => g.code))),
    );
    const genderCats = [
      ...genderPref.filter((g) => genderRaw.includes(g)),
      ...genderRaw.filter((g) => !genderPref.includes(g)),
    ];
    const genderSeries = brandData.map((d) => ({
      name: d.title,
      data: genderCats.map((c) => {
        const hit = (d.demo.gender || []).find((g) => g.code === c);
        return hit ? hit.pct : 0;
      }),
    }));

    // ---- Audience Locations (top-3 per brand, unioned) ----
    const audLocCats = Array.from(
      new Set(brandData.flatMap((d) => (d.demo.location || []).map((l) => l.bucket))),
    );
    const audLocSeries = brandData.map((d) => ({
      name: d.title,
      data: audLocCats.map((c) => {
        const hit = (d.demo.location || []).find((l) => l.bucket === c);
        return hit ? hit.pct : 0;
      }),
    }));

    // ---- Influencer Locations (top-3 per brand, unioned) ----
    const infLocCats = Array.from(
      new Set(brandData.flatMap((d) => (d.demo.influencer_location || []).map((l) => l.bucket))),
    );
    const infLocSeries = brandData.map((d) => ({
      name: d.title,
      data: infLocCats.map((c) => {
        const hit = (d.demo.influencer_location || []).find((l) => l.bucket === c);
        return hit ? hit.count : 0;
      }),
    }));

    // ---- Key insights (auto one-liners) ----
    const genderPct = (d, code) => {
      const g = (d.demo.gender || []).find((x) => x.code === code);
      return g ? g.pct : 0;
    };
    const youthPct = (d) =>
      (d.demo.age || [])
        .filter((a) => ['13-17', '18-24'].includes(a.bucket))
        .reduce((s, a) => s + a.pct, 0);

    const insights = [];
    if (brandData.length > 0) {
      const byMale = [...brandData].sort((a, b) => genderPct(b, 'Male') - genderPct(a, 'Male'));
      if (genderPct(byMale[0], 'Male') > 0) {
        insights.push(`${byMale[0].title} skews most male at ${genderPct(byMale[0], 'Male').toFixed(1)}%`);
      }
      const byFemale = [...brandData].sort((a, b) => genderPct(b, 'Female') - genderPct(a, 'Female'));
      if (genderPct(byFemale[0], 'Female') > 0) {
        insights.push(`${byFemale[0].title} has the most female-leaning audience at ${genderPct(byFemale[0], 'Female').toFixed(1)}%`);
      }
      const byYouth = [...brandData].sort((a, b) => youthPct(b) - youthPct(a));
      if (youthPct(byYouth[0]) > 0) {
        insights.push(`${byYouth[0].title} has the youngest audience — ${youthPct(byYouth[0]).toFixed(1)}% under 25`);
      }
      const topLocCounts = {};
      brandData.forEach((d) => {
        const top = (d.demo.location || [])[0];
        if (top) topLocCounts[top.bucket] = (topLocCounts[top.bucket] || 0) + 1;
      });
      const sharedTop = Object.entries(topLocCounts).sort((a, b) => b[1] - a[1])[0];
      if (sharedTop && sharedTop[1] > 1) {
        insights.push(`${sharedTop[0]} is the #1 audience city for ${sharedTop[1]} of ${brandData.length} brands`);
      }
    }

    const dataLabelFmt = (percent) => (val) =>
      val === 0 ? '' : percent ? `${Math.round(val)}%` : formatNumber(val);
    const valueAxisFmt = (percent) => (v) => (percent ? `${Math.round(v)}%` : formatNumber(v));
    // Height for horizontal grouped-bar charts: ~13px per bar (categories × brands)
    // plus room for the legend/axis, floored so small charts still look right.

    const commonOptions = {
      chart: { type: 'bar', toolbar: { show: false }, background: 'transparent' },
      colors,
      legend: {
        position: 'top',
        horizontalAlign: 'center',
        labels: { colors: '#111827', useSeriesColors: false },
        markers: { width: 8, height: 8, radius: 4 },
      },
      theme: { mode: 'light' },
    };

    // `verticalLabels` marks the many-category charts (Age, Locations). With 5
    // brands their vertical bars get too thin for readable % labels, so those use
    // HORIZONTAL bars — each brand's % sits at the end of its own row, no overlap.
    // Few-category charts (Gender) keep clean grouped vertical bars.
    const baseOptions = (categories, { percent, verticalLabels = false }) =>
      verticalLabels
        ? {
            ...commonOptions,
            plotOptions: {
              bar: { horizontal: true, borderRadius: 3, borderRadiusApplication: 'end', barHeight: '82%' },
            },
            stroke: { show: true, width: 2, colors: ['transparent'] },
            dataLabels: {
              enabled: true,
              textAnchor: 'start',
              offsetX: 4,
              style: { fontSize: '11px', fontWeight: 700, colors: ['#111827'] },
              formatter: dataLabelFmt(percent),
            },
            xaxis: {
              categories,
              max: percent ? 100 : undefined,
              labels: { style: { colors: '#9ca3af', fontSize: '11px' }, formatter: valueAxisFmt(percent) },
              axisBorder: { show: false },
              axisTicks: { show: false },
            },
            yaxis: {
              labels: { style: { colors: '#374151', fontSize: '12px', fontWeight: 600 } },
            },
            grid: {
              borderColor: '#eef2f7',
              strokeDashArray: 4,
              yaxis: { lines: { show: false } },
              padding: { left: 8, right: 28 },
            },
            tooltip: { shared: true, intersect: false, theme: 'light', y: { formatter: (v) => (percent ? `${v.toFixed(1)}%` : formatNumber(v)) } },
          }
        : {
            ...commonOptions,
            plotOptions: {
              bar: {
                borderRadius: 6,
                columnWidth: '65%',
                dataLabels: { position: 'top', orientation: 'horizontal', hideOverflowingLabels: false },
              },
            },
            dataLabels: {
              enabled: true,
              offsetY: -16,
              style: { fontSize: '11px', fontWeight: 700, colors: ['#111827'] },
              formatter: dataLabelFmt(percent),
            },
            xaxis: {
              categories,
              labels: { style: { colors: '#6b7280', fontSize: '11px' }, rotate: 0, trim: true },
            },
            yaxis: {
              max: percent ? (m) => Math.min(100, Math.ceil(m + 6)) : undefined,
              labels: { style: { colors: '#6b7280', fontSize: '11px' }, formatter: valueAxisFmt(percent) },
            },
            grid: { borderColor: '#e5e7eb', strokeDashArray: 3, xaxis: { lines: { show: false } } },
            tooltip: { shared: true, intersect: false, theme: 'light', y: { formatter: (v) => (percent ? `${v.toFixed(1)}%` : formatNumber(v)) } },
          };

    return {
      hasData: true,
      brandCount: brandData.length,
      insights,
      // Per-brand list for the shared heat-table legend (code -> name + colour).
      brands: brandData.map((b) => ({ name: b.title, color: b.color })),
      // Age & Audience Locations -> heat-tables (many categories, unreadable as bars).
      age: { series: ageSeries, cats: ageCats, empty: ageCats.length === 0, percent: true },
      audLoc: { series: audLocSeries, cats: audLocCats, empty: audLocCats.length === 0, percent: true },
      // Gender & Influencer Locations -> keep grouped bar charts (few categories, readable).
      gender: { options: baseOptions(genderCats, { percent: true }), series: genderSeries, empty: genderCats.length === 0 },
      infLoc: { options: baseOptions(infLocCats, { percent: false }), series: infLocSeries, empty: infLocCats.length === 0 },
    };
  }, [competitorAudienceDemographics, selectedBrands, competitorBrands, platform]);

  const intelligenceReportGenerating =
    reportGenerating ||
    !!(
      intelReportRemoteStatus &&
      ['pending', 'in_progress', 'processing'].includes(intelReportRemoteStatus.toLowerCase())
    );

  const intelReportStatusIsCompleted = useMemo(() => {
    const s = (intelReportRemoteStatus || '').toLowerCase();
    return ['completed', 'complete', 'success', 'done', 'ready'].includes(s);
  }, [intelReportRemoteStatus]);


  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const showReportSnack = (message, severity = 'info') => {
    setReportSnack({ open: true, message, severity });
  };

  const getHtmlPayload = (retrieveResult) => {
    if (typeof retrieveResult === 'string') return retrieveResult;
    if (retrieveResult && typeof retrieveResult.html === 'string') return retrieveResult.html;
    if (retrieveResult && typeof retrieveResult.content === 'string') return retrieveResult.content;
    throw new Error('Unexpected report response from server');
  };

  const pollUntilIntelligenceReady = async (combineIds) => {
    const maxAttempts = 120;
    const intervalMs = 2500;
    const ok = (s) =>
      ['completed', 'complete', 'success', 'done', 'ready'].includes((s || '').toLowerCase());
    const bad = (s) =>
      ['failed', 'error', 'cancelled'].includes((s || '').toLowerCase());
    for (let i = 0; i < maxAttempts; i++) {
      const data = await getIntelligenceReportStatus(combineIds);
      if (data && ok(data.status)) return data;
      if (data && bad(data.status)) {
        throw new Error(data.message || 'Report generation failed');
      }
      await sleep(intervalMs);
    }
    throw new Error('Timed out waiting for the report. Try again in a few minutes.');
  };

  const downloadIntelligenceHtmlFile = (html) => {
    const safeTitle =
      reportTitleForApi.replace(/[^\w\s-]/g, '').trim() || 'intelligence-report';
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeTitle}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const intelligenceStatusCompleted = (statusVal) =>
    ['completed', 'complete', 'success', 'done', 'ready'].includes((statusVal || '').toLowerCase());

  const handleGenerateIntelligenceReport = async (deviceType = 'desktop') => {
    setIntelReportAnchorEl(null);
    const ids = combineIdsForIntelligenceReport;
    if (!ids.length) {
      showReportSnack(
        'No brand IDs available. Open this dashboard from a saved report or ensure brands are loaded.',
        'warning',
      );
      return;
    }

    let statusData;
    try {
      statusData = await getIntelligenceReportStatus(ids);
    } catch (statusErr) {
      console.error(statusErr);
      showReportSnack('Could not check report status. Try again.', 'error');
      return;
    }

    if (intelligenceStatusCompleted(statusData?.status)) {
      if (!currentUserEmail) {
        showReportSnack(
          'Your account has no email on file. Update your profile to email the report.',
          'warning',
        );
        return;
      }
      setIntelEmailSending(true);
      try {
        await sendIntelligenceReportEmail(ids, [currentUserEmail], deviceType);
        showReportSnack('Report will be sent to your email shortly.', 'success');
        suppressIntelRemoteCompletedToastRef.current = true;
        const st = statusData?.status != null ? String(statusData.status) : null;
        setIntelReportRemoteStatus(st);
        intelPollPrevStatusRef.current = st;
      } catch (emailErr) {
        console.error(emailErr);
        showReportSnack(
          emailErr.message || 'Could not send the report by email.',
          'error',
        );
      } finally {
        setIntelEmailSending(false);
      }
      return;
    }

    setReportGenerating(true);
    try {
      const genResp = await generateIntelligenceReport(ids, reportTitleForApi, deviceType);
      if (genResp && (genResp.detail || genResp.error)) {
        const msg =
          typeof genResp.detail === 'string'
            ? genResp.detail
            : typeof genResp.error === 'string'
            ? genResp.error
            : 'Generate request failed';
        throw new Error(msg);
      }
      await pollUntilIntelligenceReady(ids);
      const raw = await retrieveIntelligenceReport(ids);
      const html = getHtmlPayload(raw);
      downloadIntelligenceHtmlFile(html);

      if (currentUserEmail) {
        try {
          await sendIntelligenceReportEmail(ids, [currentUserEmail], deviceType);
          showReportSnack('Report generated, downloaded, and emailed to you.', 'success');
        } catch (emailErr) {
          console.error(emailErr);
          showReportSnack(
            'Report generated and downloaded. Email could not be sent — try again later.',
            'warning',
          );
        }
      } else {
        showReportSnack('Report generated and downloaded.', 'success');
      }
      suppressIntelRemoteCompletedToastRef.current = true;
    } catch (e) {
      console.error(e);
      showReportSnack(e.message || 'Report generation failed', 'error');
    } finally {
      setReportGenerating(false);
      if (ids.length) {
        getIntelligenceReportStatus(ids)
          .then((data) => {
            const st = data?.status != null ? String(data.status) : null;
            setIntelReportRemoteStatus(st);
            intelPollPrevStatusRef.current = st;
          })
          .catch(() => {});
      }
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box className={classes.root} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  const showAsPercentage = false;

  return (
      <Box className={classes.root}>
        <Box
          className={`${classes.content}`}
          style={{
            flex: 1,
            transition: 'margin-left 0.3s ease',
            paddingLeft: 0,
          }}
        >
          <Container maxWidth="xl" style={{ paddingLeft: 10, paddingRight: 0 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              style={{ marginBottom: 16 }}
            >
              <div>
                <h1 style={{ fontSize: '30px', fontWeight: 700, marginBottom: 0 }}>
                  Dashboard Overview
                </h1>
                <p style={{ color: '#6b7280', marginTop: 4 }}>
                  Comprehensive analytics and insights
                </p>
              </div>

              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                {/* <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<Edit />}
                  onClick={() => {
                    const query = new URLSearchParams(location.search);
                    const editQuery = new URLSearchParams();
                    const idsParam = query.get('ids');
                    if (idsParam) editQuery.set('ids', idsParam);
                    if (platform) editQuery.set('platform', platform === 'yt' || platform === 'youtube' ? 'youtube' : platform);
                    const editPath = `/competitor-report-dashboard/edit/${id}${editQuery.toString() ? `?${editQuery.toString()}` : ''}`;
                    if (window.self !== window.top) {
                      window.parent.postMessage({
                        type: 'COMPETITOR_REPORT_NAVIGATE',
                        path: `/planner${editPath}`,
                      }, '*');
                    }
                    history.push(editPath);
                  }}
                  style={{
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    height: 40,
                  }}
                >
                  Edit Report
                </Button> */}

                {/* Date Filter */}
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <Typography
                    variant="body2"
                    style={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#111827',
                      marginRight: 8,
                    }}
                  >
                    Date Range:
                  </Typography>
                  <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    format="dd/MM/yyyy"
                    margin="normal"
                    id="start-date-picker"
                    label="Start Date"
                    value={tempStartDate}
                    onChange={(date) => handleDateChange('start', date)}
                    KeyboardButtonProps={{
                      'aria-label': 'change start date',
                    }}
                    style={{
                      margin: 0,
                      width: 180,
                    }}
                    inputProps={{
                      style: {
                        fontSize: '0.875rem',
                        padding: '8px 12px',
                      },
                    }}
                  />
                  <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    format="dd/MM/yyyy"
                    margin="normal"
                    id="end-date-picker"
                    label="End Date"
                    value={tempEndDate}
                    onChange={(date) => handleDateChange('end', date)}
                    KeyboardButtonProps={{
                      'aria-label': 'change end date',
                    }}
                    minDate={tempStartDate}
                    style={{
                      margin: 0,
                      width: 180,
                    }}
                    inputProps={{
                      style: {
                        fontSize: '0.875rem',
                        padding: '8px 12px',
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleApplyDates}
                    style={{
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      height: 40,
                      marginTop: 8,
                    }}
                  >
                    Apply
                  </Button>
                  {(tempStartDate || tempEndDate) && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleClearDates}
                      style={{
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        height: 40,
                        marginTop: 8,
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </Box>
              </MuiPickersUtilsProvider>
              </Box>
            </Box>

            {/* Brand Filter - Only show if there are multiple brands */}
            {competitorBrands.length > 0 && (
              <Box className={classes.brandFilter} style={{ marginBottom: 16, marginTop: 8 }}>
                <Typography
                  variant="body2"
                  style={{ marginRight: 8, fontWeight: 700, fontSize: '0.875rem' }}
                >
                  {isMultiBrandTab ? 'Brands:' : 'Brand:'}
                </Typography>
                {competitorBrands.map((brand, index) => {
                  const isSelected = isMultiBrandTab
                    ? selectedBrands.includes(brand.title)
                    : selectedBrand === brand.title;
                  return (
                    <Chip
                      key={brand.title}
                      label={brand.title}
                      onClick={() => handleBrandToggle(brand.title)}
                      className={classes.brandChip}
                      style={{
                        backgroundColor: isSelected ? getBrandColor(index) : 'transparent',
                        color: isSelected ? '#fff' : '#6b7280',
                        border: isSelected ? 'none' : `1px solid ${getBrandColor(index)}`,
                        fontWeight: isSelected ? 500 : 400,
                        fontSize: '0.875rem',
                        padding: '5px 12px',
                        borderRadius: '8px',
                      }}
                    />
                  );
                })}
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ marginLeft: 8, fontSize: '0.75rem', color: '#6b7280' }}
                >
                  {isMultiBrandTab
                    ? `(${selectedBrands.length} selected)`
                    : selectedBrand
                    ? `(${selectedBrand})`
                    : '(none selected)'}
                </Typography>
              </Box>
            )}

            {/* Navigation Bar with Buttons */}
            <Paper
              className={classes.tabsContainer}
              style={{ marginBottom: 0, boxShadow: 'none', backgroundColor: 'transparent' }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  style={{ gap: '8px', background: '#f3f4f6', padding: '0px 8px' }}
                >
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = tabValue === item.value;
                    return (
                      <Button
                        key={item.value}
                        onClick={(e) => {
                          e.preventDefault();
                          setTabValue(item.value);
                          if (item.value !== 0 && !selectedBrand && competitorBrands.length > 0) {
                            setSelectedBrand(competitorBrands[0].title);
                          }
                        }}
                        className={isActive ? classes.navButtonActive : classes.navButton}
                        startIcon={
                          <Icon style={{ fontSize: 18, color: isActive ? '#111827' : '#6b7280' }} />
                        }
                      >
                        {item.label}
                      </Button>
                    );
                  })}
                </Box>
                {/* Right-side action buttons */}
                {combineId && (
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    {/* Download Data */}
                    <Button
                      size="small"
                      onClick={handleDownloadData}
                      disabled={downloadLoading}
                      startIcon={downloadLoading ? <CircularProgress size={14} /> : <GetApp style={{ fontSize: 16 }} />}
                      style={{
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: 600,
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        padding: '4px 12px',
                      }}
                    >
                      Download Data
                    </Button>

                    {/* Generate Report / Email PDF — one split-button, switches on completed status */}
                    {intelReportStatusIsCompleted ? (
                      <>
                        <ButtonGroup size="small" variant="outlined" disabled={intelEmailSending}>
                          <Button
                            startIcon={intelEmailSending ? <CircularProgress size={14} /> : <Email style={{ fontSize: 16 }} />}
                            onClick={(e) => setIntelReportAnchorEl(e.currentTarget)}
                            style={{ textTransform: 'none', fontSize: 12, fontWeight: 600, borderColor: '#e5e7eb' }}
                          >
                            {intelEmailSending ? 'Sending…' : 'Email PDF'}
                          </Button>
                          <Button
                            size="small"
                            onClick={(e) => setIntelReportAnchorEl(e.currentTarget)}
                            style={{ borderColor: '#e5e7eb', padding: '0 4px' }}
                          >
                            <ArrowDropDown style={{ fontSize: 18 }} />
                          </Button>
                        </ButtonGroup>
                        <Menu
                          anchorEl={intelReportAnchorEl}
                          open={Boolean(intelReportAnchorEl)}
                          onClose={() => setIntelReportAnchorEl(null)}
                          getContentAnchorEl={null}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        >
                          <MenuItem onClick={() => handleGenerateIntelligenceReport('desktop')} style={{ fontSize: 13 }}>
                            <DesktopWindows style={{ fontSize: 16, marginRight: 8, color: '#6b7280' }} /> Desktop
                          </MenuItem>
                          <MenuItem onClick={() => handleGenerateIntelligenceReport('mobile')} style={{ fontSize: 13 }}>
                            <PhoneAndroid style={{ fontSize: 16, marginRight: 8, color: '#6b7280' }} /> Mobile
                          </MenuItem>
                        </Menu>
                      </>
                    ) : (
                      <>
                        <ButtonGroup size="small" variant="outlined" disabled={intelligenceReportGenerating}>
                          <Button
                            startIcon={intelligenceReportGenerating ? <CircularProgress size={14} /> : <Assessment style={{ fontSize: 16 }} />}
                            onClick={(e) => setIntelReportAnchorEl(e.currentTarget)}
                            style={{ textTransform: 'none', fontSize: 12, fontWeight: 600, borderColor: '#e5e7eb' }}
                          >
                            {intelligenceReportGenerating ? 'Generating…' : 'Generate Report'}
                          </Button>
                          <Button
                            size="small"
                            onClick={(e) => setIntelReportAnchorEl(e.currentTarget)}
                            style={{ borderColor: '#e5e7eb', padding: '0 4px' }}
                          >
                            <ArrowDropDown style={{ fontSize: 18 }} />
                          </Button>
                        </ButtonGroup>
                        <Menu
                          anchorEl={intelReportAnchorEl}
                          open={Boolean(intelReportAnchorEl)}
                          onClose={() => setIntelReportAnchorEl(null)}
                          getContentAnchorEl={null}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        >
                          <MenuItem onClick={() => handleGenerateIntelligenceReport('desktop')} style={{ fontSize: 13 }}>
                            <DesktopWindows style={{ fontSize: 16, marginRight: 8, color: '#6b7280' }} /> Desktop
                          </MenuItem>
                          <MenuItem onClick={() => handleGenerateIntelligenceReport('mobile')} style={{ fontSize: 13 }}>
                            <PhoneAndroid style={{ fontSize: 16, marginRight: 8, color: '#6b7280' }} /> Mobile
                          </MenuItem>
                        </Menu>
                      </>
                    )}

                    {/* Auto-Track */}
                    <Button
                      size="small"
                      onClick={() => setReportHistoryOpen(!reportHistoryOpen)}
                      startIcon={
                        <Box style={{ position: 'relative', display: 'flex' }}>
                          <Schedule style={{ fontSize: 18, color: autoTrackEnabled ? '#548c2f' : '#d97706' }} />
                          <span
                            style={{
                              position: 'absolute',
                              top: -2,
                              right: -3,
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              backgroundColor: autoTrackEnabled ? '#16a34a' : '#f59e0b',
                              border: '1.5px solid #fff',
                            }}
                          />
                        </Box>
                      }
                      style={{
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: 600,
                        color: autoTrackEnabled ? '#548c2f' : '#d97706',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        padding: '4px 12px',
                        minWidth: 0,
                      }}
                    >
                      Auto-Track
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Overview Tab */}
            {tabValue === 0 && (
              <Box style={{ marginTop: 16 }}>
                {/* Metric Cards - Dashboard Style - All in one line */}
                <Box
                  display="flex"
                  gap={3}
                  style={{
                    marginBottom: '10px',
                    flexWrap: 'nowrap',
                    overflowX: 'auto',
                    gap: '16px',
                    padding: '10px',
                  }}
                  sx={{
                    '& > *': {
                      flex: '1 1 0',
                      minWidth: 0,
                    },
                    '@media (max-width: 1200px)': {
                      flexWrap: 'wrap',
                    },
                  }}
                >
                  <DashboardMetricCard
                    icon={Description}
                    title="Total Posts"
                    value={formatMetricValue(
                      (!isSingleBrandView ? calculatedMetrics : metricsData).totalPosts,
                      'large',
                    )}
                    subtitle={platformDisplayName}
                    trend={brandInsights?.metrics?.total_posts_change || '+12.5%'}
                    iconColor="#fef3c7"
                  />
                  <DashboardMetricCard
                    icon={ShowChart}
                    title="Avg Engagement Rate"
                    value={formatPercentage(
                      (!isSingleBrandView ? calculatedMetrics : metricsData).avgEngagementRate,
                    )}
                    subtitle={platformDisplayName}
                    trend={brandInsights?.metrics?.engagement_rate_change || '+8.2%'}
                    iconColor="#fef3c7"
                  />
                  <DashboardMetricCard
                    icon={People}
                    title="Total Influencers"
                    value={(!isSingleBrandView
                      ? calculatedMetrics
                      : metricsData
                    ).totalInfluencers.toString()}
                    subtitle={platformDisplayName}
                    trend={brandInsights?.metrics?.influencers_change || '+15.3%'}
                    iconColor="#f1fec7ff"
                  />
                  <DashboardMetricCard
                    icon={Visibility}
                    title="Total Views"
                    value={formatMetricValue(
                      (!isSingleBrandView ? calculatedMetrics : metricsData).reelViews,
                      'large',
                    )}
                    subtitle={platformDisplayName}
                    trend={brandInsights?.metrics?.views_change || '+24.5%'}
                    iconColor="#fef3c7"
                  />
                  <DashboardMetricCard
                    icon={Public}
                    title="Total Reach"
                    value={formatMetricValue(
                      (!isSingleBrandView ? calculatedMetrics : metricsData).reelViews * 1.37,
                      'large',
                    )}
                    subtitle={platformDisplayName}
                    trend={brandInsights?.metrics?.reach_change || '+16.8%'}
                    iconColor="#fef3c7"
                  />
                </Box>

                {/* Brand Performance Comparison Chart */}
                {selectedBrands.length > 0 && (
                  <Grid container spacing={3} style={{ marginBottom: 24, marginTop: 0 }}>
                    <Grid item xs={12}>
                      <Card
                        className={classes.card}
                        style={{
                          borderLeft: '4px solid #9333ea',
                          boxShadow:
                            '0 1px 11px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                        }}
                      >
                        <CardHeader
                          title={
                            <Box className={classes.cardHeader}>
                              <Box
                                className={classes.iconContainer}
                                style={{
                                  background: `linear-gradient(135deg, ${COLORS[0]}CC, ${COLORS[1]}80)`,
                                }}
                              >
                                <BarChartIcon style={{ color: '#fff', fontSize: 35 }} />
                              </Box>
                              <div>
                                <h3
                                  style={{
                                    fontSize: '24px',
                                    fontWeight: 600,
                                    marginBottom: 0,
                                    marginTop: '0px',
                                    lineHeight: '1.2',
                                  }}
                                >
                                  Brand Performance Comparison
                                </h3>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  style={{
                                    fontSize: '0.75rem',
                                    marginTop: '0px',
                                    display: 'block',
                                  }}
                                >
                                  All key metrics across brands
                                </Typography>
                              </div>
                            </Box>
                          }
                        />
                        <CardContent>
                          <Chart
                            options={{
                              chart: {
                                type: 'bar',
                                toolbar: { show: false },
                                background: 'transparent',
                                redrawOnWindowResize: true,
                                redrawOnParentResize: true,
                                offsetY: 0,
                              },
                              plotOptions: {
                                bar: {
                                  borderRadius: 9,
                                  columnWidth: '60%',
                                  dataLabels: {
                                    position: 'top',
                                    hideOverflowingLabels: false,
                                  },
                                },
                              },
                              responsive: [
                                {
                                  breakpoint: 1200,
                                  options: {
                                    chart: {
                                      height: 320,
                                    },
                                    plotOptions: {
                                      bar: {
                                        columnWidth: "60%",
                                      },
                                    },
                                  },
                                },
                                {
                                  breakpoint: 992,
                                  options: {
                                    chart: {
                                      height: 300,
                                    },
                                    plotOptions: {
                                      bar: {
                                        columnWidth: "75%",
                                      },
                                    },
                                  },
                                },
                                {
                                  breakpoint: 768,
                                  options: {
                                    chart: {
                                      height: 280,
                                    },
                                    plotOptions: {
                                      bar: {
                                        columnWidth: "80%",
                                      },
                                    },
                                    xaxis: {
                                      labels: {
                                        rotate: -45,
                                      },
                                    },
                                  },
                                },
                                {
                                  breakpoint: 480,
                                  options: {
                                    chart: {
                                      height: 250,
                                    },
                                    plotOptions: {
                                      bar: {
                                        columnWidth: "90%",
                                      },
                                    },
                                    xaxis: {
                                      labels: {
                                        rotate: -45,
                                        trim: true,
                                      },
                                    },
                                  },
                                },
                              ],
                              dataLabels: {
                                enabled: true,
                                style: {
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  colors: ['#111827'],
                                },
                                offsetY: -20,
                                formatter: function (val) {
                                  if (val === 0) return '';
                                  return formatNumber(val);
                                },
                              },
                              xaxis: {
                                categories: brandComparisonData.map((d) => d.brand),
                                labels: {
                                  style: {
                                    colors: '#6b7280',
                                    fontSize: '12px',
                                  },
                                },
                              },
                              yaxis: {
                                title: {
                                  text: 'Metric Values',
                                  style: {
                                    color: '#6b7280',
                                    fontSize: '12px',
                                  },
                                },
                                labels: {
                                  style: {
                                    colors: '#6b7280',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                  },
                                  formatter: (val) => formatNumber(val),
                                },
                              },
                              colors: brandComparisonData.map((d, index) => getBrandColor(index)),
                              legend: {
                                position: 'top',
                                horizontalAlign: 'center',
                                labels: {
                                  colors: '#111827',
                                  fontSize: '12px',
                                  useSeriesColors: false,
                                },
                                markers: {
                                  width: 8,
                                  height: 8,
                                  radius: 4,
                                  fillColors: ['#f9a620', '#ffd449', '#f9a620', '#ffd449'],
                                },
                                itemMargin: {
                                  horizontal: 16,
                                  vertical: 0,
                                },
                              },
                              grid: {
                                borderColor: '#e5e7eb',
                                strokeDashArray: 3,
                                xaxis: {
                                  lines: {
                                    show: false,
                                  },
                                },
                                yaxis: {
                                  lines: {
                                    show: true,
                                  },
                                },
                              },
                              theme: { mode: 'light' },
                              tooltip: {
                                shared: true,
                                intersect: false,
                                theme: 'light',
                              },
                            }}
                            series={[
                              {
                                name: 'Creators',
                                data: brandComparisonData.map((d) => d.creatorCount),
                              },
                              {
                                name: 'Engagement',
                                data: brandComparisonData.map((d) => d.engagement),
                              },
                              {
                                name: 'Posts',
                                data: brandComparisonData.map((d) => d.posts),
                              },
                              {
                                name: 'Views',
                                data: brandComparisonData.map((d) => d.views),
                              },
                            ]}
                            type="bar"
                            height={400}
                          />

                          {/* Key Insights Section - All in Grid */}
                          <Box className={classes.insightBox}>
                            <Box className={classes.insightHeader}>
                              <TrendingUp style={{ color: '#10b981', fontSize: 16 }} />
                              <Typography variant="subtitle2" style={{ fontWeight: 500 }}>
                                Key Insights
                              </Typography>
                            </Box>

                            {/* All Insights in 2x2 Grid */}
                            <Grid container spacing={2} style={{ marginTop: 16 }}>
                              <Grid item xs={12} sm={6}>
                                <Typography className={classes.insightText}>
                                  <strong style={{ color: '#000' }}>Creators:</strong>{' '}
                                  {generateBrandComparisonInsight(
                                    brandComparisonData,
                                    'creatorCount',
                                  )}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography className={classes.insightText}>
                                  <strong style={{ color: '#000' }}>Engagement:</strong>{' '}
                                  {generateBrandComparisonInsight(
                                    brandComparisonData,
                                    'engagement',
                                  )}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography className={classes.insightText}>
                                  <strong style={{ color: '#000' }}>Posts:</strong>{' '}
                                  {generateBrandComparisonInsight(brandComparisonData, 'posts')}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography className={classes.insightText}>
                                  <strong style={{ color: '#000' }}>Views:</strong>{' '}
                                  {generateBrandComparisonInsight(brandComparisonData, 'views')}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}

                {/* Analytics Charts Section */}
                <Grid container spacing={3} style={{ marginTop: 24 }}>
                  {/* Share of Voice */}
                  <Grid item xs={12} md={6}>
                    <Card className={classes.card}>
                      <CardHeader
                        title={
                          <Box className={classes.cardHeader}>
                            <Box
                              className={classes.iconContainer}
                              style={{
                                background: `linear-gradient(135deg, ${COLORS[0]}CC, ${COLORS[2]}80)`,
                              }}
                            >
                              <PieChartIcon style={{ color: '#fff', fontSize: 20 }} />
                            </Box>
                            <Box>
                              <div
                                style={{
                                  fontSize: '24px',
                                  fontWeight: 600,
                                  marginBottom: 0,
                                  marginTop: '0px',
                                  lineHeight: '1.2',
                                }}
                              >
                                Share of Voice
                              </div>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ fontSize: '0.75rem', marginTop: '0px', display: 'block' }}
                              >
                                Brand distribution by Views
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                      <CardContent>
                        <Chart
                          options={shareOfVoiceChart.options}
                          series={shareOfVoiceChart.series}
                          type="donut"
                          height={300}
                        />
                        <Box className={classes.insightBox}>
                          <Box className={classes.insightHeader}>
                            <TrendingUp style={{ color: '#10b981', fontSize: 16 }} />
                            <Typography variant="subtitle2" style={{ fontWeight: 500 }}>
                              Key Insight
                            </Typography>
                          </Box>
                          <Typography className={classes.insightText}>
                            {generateShareOfVoiceInsight(filteredShareOfVoiceData)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Market Position */}
                  <Grid item xs={12} md={6}>
                    <Card className={classes.card}>
                      <CardHeader
                        title={
                          <Box className={classes.cardHeader}>
                            <Box
                              className={classes.iconContainer}
                              style={{
                                background: `linear-gradient(135deg, ${COLORS[1]}CC, ${COLORS[3]}80)`,
                              }}
                            >
                              <GpsFixed style={{ color: '#fff', fontSize: 20 }} />
                            </Box>
                            <Box>
                              <div
                                style={{
                                  fontSize: '24px',
                                  fontWeight: 600,
                                  marginBottom: 0,
                                  marginTop: '0px',
                                  lineHeight: '1.2',
                                }}
                              >
                                Market Position
                              </div>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ fontSize: '0.75rem', marginTop: '0px', display: 'block' }}
                              >
                                Engagement rate vs influencer count
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                      <CardContent>
                        <Chart
                          key={`market-position-${selectedBrands.join('-')}-${
                            filteredMarketPositionData.length
                          }`}
                          options={marketPositionChart.options}
                          series={marketPositionChart.series}
                          type="scatter"
                          height={300}
                        />
                        <Box className={classes.insightBox}>
                          <Box className={classes.insightHeader}>
                            <TrendingUp style={{ color: '#10b981', fontSize: 16 }} />
                            <Typography variant="subtitle2" style={{ fontWeight: 500 }}>
                              Market Positioning
                            </Typography>
                          </Box>
                          <Typography className={classes.insightText}>
                            Bubble size represents post volume. Brands in the upper-right quadrant
                            have both high influencer counts and strong engagement rates.
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Category Breakdown */}
                  <Grid item xs={12}>
                    <Card className={classes.card}>
                      <CardHeader
                        title={
                          <Box className={classes.cardHeader}>
                            <Box
                              className={classes.iconContainer}
                              style={{
                                background: `linear-gradient(135deg, ${COLORS[0]}CC, ${COLORS[2]}80)`,
                              }}
                            >
                              <BarChartIcon style={{ color: '#fff', fontSize: 20 }} />
                            </Box>
                            <Box>
                              <div
                                style={{
                                  fontSize: '24px',
                                  fontWeight: 600,
                                  marginBottom: 0,
                                  marginTop: '0px',
                                  lineHeight: '1.2',
                                }}
                              >
                                Genre Breakdown
                              </div>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ fontSize: '0.75rem', marginTop: '0px', display: 'block' }}
                              >
                                Content distribution by genre and brand
                              </Typography>
                            </Box>
                          </Box>
                        }
                        action={
                          <CategoryBreakdownTable
                            selectedBrands={selectedBrands}
                            competitorBrands={competitorBrands}
                            start_date={start_date}
                            end_date={end_date}
                            showAsPercentage={showAsPercentage}
                            renderButtonsOnly={true}
                            selectedMetric={categoryBreakdownMetric}
                            onMetricChange={setCategoryBreakdownMetric}
                            platform={platform}
                          />
                        }
                      />
                      <CardContent>
                        <CategoryBreakdownTable
                          selectedBrands={selectedBrands}
                          competitorBrands={competitorBrands}
                          start_date={start_date}
                          end_date={end_date}
                          showAsPercentage={showAsPercentage}
                          renderButtonsOnly={false}
                          selectedMetric={categoryBreakdownMetric}
                          onMetricChange={setCategoryBreakdownMetric}
                          platform={platform}
                        />
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Influencer Overlap Section */}
                  <Grid item xs={12}>
                    <Card className={classes.card}>
                      <CardHeader
                        title={
                          <Box className={classes.cardHeader}>
                            <Box
                              className={classes.iconContainer}
                              style={{
                                background: `linear-gradient(135deg, ${COLORS[2]}CC, ${COLORS[3]}80)`,
                              }}
                            >
                              <People style={{ color: '#fff', fontSize: 20 }} />
                            </Box>
                            <Box>
                              <div
                                style={{
                                  fontSize: '24px',
                                  fontWeight: 600,
                                  marginBottom: 0,
                                  marginTop: '0px',
                                  lineHeight: '1.2',
                                }}
                              >
                                Influencer Overlap
                              </div>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ fontSize: '0.75rem', marginTop: '0px', display: 'block' }}
                              >
                                Exclusive vs Shared influencers
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                      <CardContent>
                        <InfluencerOverlapSection
                          selectedBrands={selectedBrands}
                          influencerOverlapData={filteredInfluencerOverlapData}
                          influencersData={influencersData}
                          showAsPercentage={showAsPercentage}
                          summary={summary}
                          competitorBrands={competitorBrands}
                          start_date={start_date}
                          end_date={end_date}
                          platform={platform}
                        />
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Row 5: Influencer Category Distribution (Full Width) */}
                  <Grid item xs={12}>
                    <Card className={classes.card} style={{ borderLeft: `4px solid #10b981` }}>
                      <CardHeader
                        title={
                          <Box className={classes.cardHeader}>
                            <Box
                              className={classes.iconContainer}
                              style={{
                                background: `linear-gradient(135deg, ${COLORS[3]}CC, ${COLORS[3]}80)`,
                              }}
                            >
                              <GridOn style={{ color: '#fff', fontSize: 24 }} />
                            </Box>
                            <Box>
                              <div
                                style={{
                                  fontSize: '24px',
                                  fontWeight: 600,
                                  marginBottom: 0,
                                  marginTop: '0px',
                                  lineHeight: '1.2',
                                }}
                              >
                                Influencer Category Distribution
                              </div>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ fontSize: '0.75rem', marginTop: '0px', display: 'block' }}
                              >
                                Distribution across follower tiers by metric
                              </Typography>
                            </Box>
                          </Box>
                        }
                        action={
                          <InfluencerCategoryDistribution
                            selectedBrands={selectedBrands}
                            summary={summary}
                            showAsPercentage={showAsPercentage}
                            renderButtonsOnly={true}
                            categoryMetric={influencerCategoryMetric}
                            onCategoryMetricChange={setInfluencerCategoryMetric}
                          />
                        }
                      />
                      <CardContent>
                        <InfluencerCategoryDistribution
                          selectedBrands={selectedBrands}
                          summary={summary}
                          showAsPercentage={showAsPercentage}
                          renderButtonsOnly={false}
                          categoryMetric={influencerCategoryMetric}
                          onCategoryMetricChange={setInfluencerCategoryMetric}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

              {/* Audience Demographics & Geography — per-brand comparison */}
              {demographicsComparison.hasData && (
                <Box style={{ marginTop: 24 }}>
                  <Box style={{ marginBottom: 12 }}>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Typography variant="h6" style={{ fontWeight: 700 }}>
                        Audience Demographics &amp; Geography
                      </Typography>
                      <Chip
                        label="Beta"
                        size="small"
                        style={{
                          height: 20,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          backgroundColor: '#ede9fe',
                          color: '#6d28d9',
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      Per-brand audience breakdown across {demographicsComparison.brandCount} brand
                      {demographicsComparison.brandCount === 1 ? '' : 's'}
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    {/* Age Distribution */}
                    {!demographicsComparison.age.empty && (
                      <Grid item xs={12} md={6}>
                        <Card className={classes.card} style={{ height: '100%' }}>
                          <CardHeader
                            title={<Typography variant="subtitle1" style={{ fontWeight: 600 }}>Age Distribution</Typography>}
                            subheader={
                              <Typography variant="caption" color="textSecondary">
                                Derived from limited data — may not be 100% accurate.
                              </Typography>
                            }
                            style={{ paddingBottom: 0 }}
                          />
                          <CardContent>
                            <DemographicHeatTable
                              categories={demographicsComparison.age.cats}
                              series={demographicsComparison.age.series}
                              percent={demographicsComparison.age.percent}
                            />
                            <BrandLegend brands={demographicsComparison.brands} />
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    {/* Gender Distribution */}
                    {!demographicsComparison.gender.empty && (
                      <Grid item xs={12} md={6}>
                        <Card className={classes.card} style={{ height: '100%' }}>
                          <CardHeader
                            title={<Typography variant="subtitle1" style={{ fontWeight: 600 }}>Gender Distribution</Typography>}
                            subheader={
                              <Typography variant="caption" color="textSecondary">
                                Derived from limited data — may not be 100% accurate.
                              </Typography>
                            }
                            style={{ paddingBottom: 0 }}
                          />
                          <CardContent>
                            <Chart
                              options={demographicsComparison.gender.options}
                              series={demographicsComparison.gender.series}
                              type="bar"
                              height={300}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    {/* Top Audience Locations */}
                    {!demographicsComparison.audLoc.empty && (
                      <Grid item xs={12} md={6}>
                        <Card className={classes.card} style={{ height: '100%' }}>
                          <CardHeader
                            title={<Typography variant="subtitle1" style={{ fontWeight: 600 }}>Top Audience Locations</Typography>}
                            subheader={
                              <Typography variant="caption" color="textSecondary">
                                Derived from limited data — may not be 100% accurate.
                              </Typography>
                            }
                            style={{ paddingBottom: 0 }}
                          />
                          <CardContent>
                            <DemographicHeatTable
                              categories={demographicsComparison.audLoc.cats}
                              series={demographicsComparison.audLoc.series}
                              percent={demographicsComparison.audLoc.percent}
                            />
                            <BrandLegend brands={demographicsComparison.brands} />
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    {/* Influencer Locations */}
                    {!demographicsComparison.infLoc.empty && (
                      <Grid item xs={12} md={6}>
                        <Card className={classes.card} style={{ height: '100%' }}>
                          <CardHeader
                            title={<Typography variant="subtitle1" style={{ fontWeight: 600 }}>Influencer Locations</Typography>}
                            subheader={
                              <Typography variant="caption" color="textSecondary">
                                Derived from limited data — may not be 100% accurate.
                              </Typography>
                            }
                            style={{ paddingBottom: 0 }}
                          />
                          <CardContent>
                            <Chart
                              options={demographicsComparison.infLoc.options}
                              series={demographicsComparison.infLoc.series}
                              type="bar"
                              height={300}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Grid>

                  {/* Key Insights */}
                  {demographicsComparison.insights.length > 0 && (
                    <Box className={classes.insightBox} style={{ marginTop: 16 }}>
                      <Box className={classes.insightHeader}>
                        <TrendingUp style={{ color: '#10b981', fontSize: 16 }} />
                        <Typography variant="subtitle2" style={{ fontWeight: 500 }}>
                          Key Insights
                        </Typography>
                      </Box>
                      <Box component="ul" style={{ margin: 0, paddingLeft: 18 }}>
                        {demographicsComparison.insights.map((text, i) => (
                          <li key={i}>
                            <Typography variant="body2" className={classes.insightText}>
                              {text}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Post Format Analysis (moved to end of page) */}
              <Box style={{ marginTop: 40, clear: 'both', position: 'relative', zIndex: 1 }}>
                  <Card className={classes.card} style={{ borderLeft: `4px solid #3b82f6`, height: 'auto' }}>
                    <CardHeader
                      title={
                        <Box className={classes.cardHeader}>
                          <Box
                            className={classes.iconContainer}
                            style={{
                              background: `linear-gradient(135deg, ${COLORS[0]}CC, ${COLORS[0]}80)`,
                            }}
                          >
                            <Videocam style={{ color: '#fff', fontSize: 24 }} />
                          </Box>
                          <Box>
                            <div
                              style={{
                                fontSize: '24px',
                                fontWeight: 600,
                                marginBottom: 0,
                                marginTop: '0px',
                                lineHeight: '1.2',
                              }}
                            >
                              Post Format Analysis
                            </div>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              style={{ fontSize: '0.75rem', marginTop: '0px', display: 'block' }}
                            >
                              {platformDisplayName} content format distribution{' '}
                              {platform === 'yt' || platform === 'youtube'
                                ? '(Shorts & Video)'
                                : '(Reels & Posts)'}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                    <CardContent>
                      <PostFormatAnalysis
                        selectedBrands={selectedBrands}
                        competitorBrands={competitorBrands}
                        start_date={start_date}
                        end_date={end_date}
                        showAsPercentage={showAsPercentage}
                        platform={platform}
                      />
                    </CardContent>
                  </Card>
              </Box>
            </Box>
            )}

            {/* Influencers Tab */}
            {tabValue === 1 && (
              <InfluencersTab
                selectedBrand={selectedBrand}
                competitorBrands={competitorBrands}
                start_date={resolvedStartDate}
                end_date={resolvedEndDate}
                platform={platform}
              />
            )}

            {/* Posts Tab */}
            {tabValue === 2 && (
              <PostsTab
                selectedBrand={selectedBrand}
                competitorBrands={competitorBrands}
                start_date={resolvedStartDate}
                end_date={resolvedEndDate}
                platform={platform}
              />
            )}

            {/* Content Themes & Hooks Tab */}
            {tabValue === 3 && (
              <ContentThemesTab
                selectedBrands={selectedBrands}
                competitorBrands={competitorBrands}
                start_date={resolvedStartDate}
                end_date={resolvedEndDate}
                platform={platform}
              />
            )}

            <Snackbar
              open={reportSnack.open}
              autoHideDuration={8000}
              onClose={() => setReportSnack((s) => ({ ...s, open: false }))}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert
                onClose={() => setReportSnack((s) => ({ ...s, open: false }))}
                severity={reportSnack.severity}
              >
                {reportSnack.message}
              </Alert>
            </Snackbar>
          </Container>
        </Box>

        {/* Auto-Track Modal */}
        <Dialog
          open={reportHistoryOpen}
          onClose={() => setReportHistoryOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle style={{ paddingBottom: 0 }}>
            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Schedule style={{ fontSize: 22, color: autoTrackEnabled ? '#548c2f' : '#d97706' }} />
                <Typography variant="h6" style={{ fontWeight: 600 }}>Auto-Track</Typography>
                <Chip
                  size="small"
                  label={autoTrackEnabled ? 'Active' : 'Paused'}
                  style={{
                    backgroundColor: autoTrackEnabled ? '#dcfce7' : '#fef3c7',
                    color: autoTrackEnabled ? '#166534' : '#92400e',
                    fontWeight: 600, fontSize: 11,
                  }}
                />
              </Box>
              <Box style={{ display: 'flex', gap: 6 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handlePauseResume}
                  disabled={pauseLoading || runNowLoading || autoTrackRunInProgress}
                  className={(runNowLoading || autoTrackRunInProgress) && !pauseLoading ? classes.autoTrackHeaderBtnOutlinedMuted : undefined}
                  startIcon={pauseLoading ? <CircularProgress size={12} style={{ color: 'inherit' }} /> : null}
                  style={{
                    textTransform: 'none',
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: 6,
                    ...((runNowLoading || autoTrackRunInProgress) && !pauseLoading
                      ? {}
                      : {
                          color: autoTrackEnabled ? '#548c2f' : '#d97706',
                          borderColor: autoTrackEnabled ? '#bbf7d0' : '#fde68a',
                        }),
                  }}
                >
                  {pauseLoading ? (autoTrackEnabled ? 'Pausing…' : 'Resuming…') : autoTrackEnabled ? 'Pause' : 'Resume'}
                </Button>
                <ButtonGroup
                  size="small"
                  variant="contained"
                  disabled={runNowLoading || autoTrackRunInProgress}
                  className={runNowLoading || autoTrackRunInProgress ? classes.autoTrackHeaderBtnContainedMuted : undefined}
                  style={{ boxShadow: 'none', borderRadius: 6 }}
                >
                  <Button
                    startIcon={
                      runNowLoading ? (
                        <CircularProgress size={12} style={{ color: '#9ca3af' }} />
                      ) : (
                        <PlayArrow style={{ fontSize: 16, color: runNowLoading || autoTrackRunInProgress ? '#9ca3af' : '#fff' }} />
                      )
                    }
                    onClick={(e) => setRunNowAnchorEl(e.currentTarget)}
                    style={{
                      textTransform: 'none',
                      fontSize: 12,
                      fontWeight: 600,
                      boxShadow: 'none',
                      ...(runNowLoading || autoTrackRunInProgress ? {} : { backgroundColor: '#548c2f', color: '#fff' }),
                    }}
                  >
                    {runNowLoading ? 'Starting…' : autoTrackRunInProgress ? 'In progress…' : 'Run Now'}
                  </Button>
                  <Button
                    size="small"
                    onClick={(e) => setRunNowAnchorEl(e.currentTarget)}
                    style={{
                      boxShadow: 'none',
                      padding: '0 4px',
                      ...(runNowLoading || autoTrackRunInProgress ? {} : { backgroundColor: '#548c2f', color: '#fff', borderLeft: '1px solid #3d7a24' }),
                    }}
                  >
                    <ArrowDropDown style={{ fontSize: 18 }} />
                  </Button>
                </ButtonGroup>
                <Menu
                  anchorEl={runNowAnchorEl}
                  open={Boolean(runNowAnchorEl)}
                  onClose={() => setRunNowAnchorEl(null)}
                  getContentAnchorEl={null}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <MenuItem onClick={() => handleRunNow('desktop')} style={{ fontSize: 13 }}>
                    <DesktopWindows style={{ fontSize: 16, marginRight: 8, color: '#6b7280' }} /> Desktop
                  </MenuItem>
                  <MenuItem onClick={() => handleRunNow('mobile')} style={{ fontSize: 13 }}>
                    <PhoneAndroid style={{ fontSize: 16, marginRight: 8, color: '#6b7280' }} /> Mobile
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent dividers style={{ padding: 0 }}>
            <Tabs
              value={autoTrackTab === 'history' ? 0 : 1}
              onChange={(_e, v) => setAutoTrackTab(v === 0 ? 'history' : 'settings')}
              indicatorColor="primary"
              style={{ borderBottom: '1px solid #f3f4f6', paddingLeft: 16 }}
            >
              <Tab label="History" style={{ textTransform: 'none', fontWeight: 600, fontSize: 13, minWidth: 80 }} />
              <Tab label="Settings" style={{ textTransform: 'none', fontWeight: 600, fontSize: 13, minWidth: 80 }} />
            </Tabs>

            {/* History */}
            {autoTrackTab === 'history' && (
              <Box style={{ maxHeight: 360, overflowY: 'auto' }}>
                {historyLoading ? (
                  <Box style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : reportHistory.length === 0 ? (
                  <Box style={{ padding: 32, textAlign: 'center' }}>
                    <Typography style={{ fontSize: 13, color: '#6b7280' }}>No runs yet.</Typography>
                  </Box>
                ) : reportHistory.map((run) => {
                  const runDate = run.scheduled_at ? moment(run.scheduled_at).format('MMM D, YYYY') : '—';
                  const freqLabel = run.frequency ? run.frequency.replace('_', '-') : '';
                  const formatLabel = [run.pdf_attachment && 'PDF', run.dashboard_link && 'Dashboard link'].filter(Boolean).join(' + ');
                  const isAudit = ['paused', 'resumed', 'settings_changed'].includes(run.status);
                  const dotColor = {
                    delivered: '#16a34a', failed: '#dc2626', scheduled: '#f59e0b',
                    in_progress: '#3b82f6', paused: '#9ca3af', resumed: '#548c2f', settings_changed: '#6366f1', cancelled: '#d1d5db',
                  }[run.status] || '#f59e0b';
                  const chipStyle = {
                    delivered:        { bg: '#dcfce7', color: '#166534' },
                    failed:           { bg: '#fef2f2', color: '#dc2626' },
                    scheduled:        { bg: '#fef3c7', color: '#92400e' },
                    in_progress:      { bg: '#dbeafe', color: '#1d4ed8' },
                    paused:           { bg: '#f3f4f6', color: '#6b7280' },
                    resumed:          { bg: '#dcfce7', color: '#166534' },
                    settings_changed: { bg: '#ede9fe', color: '#6d28d9' },
                    cancelled:        { bg: '#f3f4f6', color: '#9ca3af' },
                  }[run.status] || { bg: '#fef3c7', color: '#92400e' };
                  const chipLabel = {
                    delivered: 'Delivered', failed: 'Failed', scheduled: 'Scheduled',
                    in_progress: 'In Progress', paused: 'Paused', resumed: 'Resumed',
                    settings_changed: 'Changed', cancelled: 'Cancelled',
                  }[run.status] || run.status;
                  const subtitle = isAudit
                    ? (run.note || chipLabel)
                    : run.status === 'delivered'
                    ? `Sent to ${run.recipients.length} recipients • ${formatLabel}${run.post_count ? ` • ${run.post_count} posts` : ''}`
                    : run.status === 'failed'
                    ? `Failed: ${run.error_message || 'Unknown error'}`
                    : `Scheduled • ${run.recipients.length} recipients`;
                  return (
                    <Box
                      key={run.id}
                      style={{
                        display: 'flex', alignItems: 'center',
                        padding: '12px 20px', borderBottom: '1px solid #f9fafb', gap: 14,
                        opacity: isAudit ? 0.75 : 1,
                      }}
                    >
                      <Box style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, backgroundColor: dotColor }} />
                      <Box style={{ flex: 1 }}>
                        <Typography style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                          {runDate}{!isAudit && freqLabel ? ` — ${freqLabel.charAt(0).toUpperCase() + freqLabel.slice(1)} Report` : ''}
                        </Typography>
                        <Typography style={{ fontSize: 12, color: '#6b7280' }}>{subtitle}</Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={chipLabel}
                        style={{ fontWeight: 600, fontSize: 11, backgroundColor: chipStyle.bg, color: chipStyle.color }}
                      />
                      <Box style={{ display: 'flex', gap: 4 }}>
                        {run.status === 'delivered' && (
                          <>
                            {run.html_file_path && (
                              <Button size="small" variant="outlined" href={run.html_file_path} target="_blank" style={{ fontSize: 11, textTransform: 'none', minWidth: 0, padding: '2px 10px', borderRadius: 4 }}>View</Button>
                            )}
                            {run.pdf_file_path && (
                              <Button size="small" variant="outlined" href={run.pdf_file_path} target="_blank" style={{ fontSize: 11, textTransform: 'none', minWidth: 0, padding: '2px 10px', borderRadius: 4 }}>PDF</Button>
                            )}
                          </>
                        )}
                        {run.status === 'failed' && (
                          <Button size="small" variant="outlined" onClick={handleRunNow} style={{ fontSize: 11, textTransform: 'none', minWidth: 0, padding: '2px 10px', borderRadius: 4 }}>Retry</Button>
                        )}
                        {run.status === 'scheduled' && (
                          <Button size="small" variant="outlined" style={{ fontSize: 11, textTransform: 'none', minWidth: 0, padding: '2px 10px', borderRadius: 4, color: '#dc2626', borderColor: '#fecaca' }}>Cancel</Button>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Settings */}
            {autoTrackTab === 'settings' && (
              <Box style={{ padding: 20 }}>
                <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                  <Box>
                    <Typography style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Frequency</Typography>
                    <TextField
                      select fullWidth variant="outlined" size="small"
                      value={atFrequency} onChange={(e) => setAtFrequency(e.target.value)}
                      SelectProps={{ native: true }}
                    >
                      <option value="weekly">Weekly</option>
                      <option value="bi_weekly">Bi-Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                    </TextField>
                  </Box>
                  <Box>
                    <Typography style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Day of Month</Typography>
                    <TextField
                      select fullWidth variant="outlined" size="small"
                      value={atDay} onChange={(e) => setAtDay(Number(e.target.value))}
                      SelectProps={{ native: true }}
                    >
                      <option value={1}>1st</option>
                      <option value={5}>5th</option>
                      <option value={10}>10th</option>
                      <option value={15}>15th</option>
                      <option value={20}>20th</option>
                      <option value={25}>25th</option>
                    </TextField>
                  </Box>
                </Box>

                <Box style={{ marginBottom: 20 }}>
                  <Typography style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Recipients</Typography>
                  <Box style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 12px', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', minHeight: 40 }}>
                    {atRecipients.map((email) => (
                      <Chip
                        key={email} label={email} size="small"
                        onDelete={() => setAtRecipients(atRecipients.filter((r) => r !== email))}
                        style={{ backgroundColor: '#eff6ff', color: '#1d4ed8' }}
                      />
                    ))}
                    <input
                      type="email" value={atEmailInput}
                      onChange={(e) => setAtEmailInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && atEmailInput.trim()) {
                          const email = atEmailInput.trim();
                          if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !atRecipients.includes(email)) {
                            setAtRecipients([...atRecipients, email]);
                          }
                          setAtEmailInput('');
                        }
                      }}
                      placeholder="Type email and press Enter..."
                      style={{ border: 'none', outline: 'none', fontSize: 13, flex: 1, minWidth: 180, color: '#6b7280', padding: '4px 0' }}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Report Format</Typography>
                  <Box style={{ display: 'flex', gap: 16 }}>
                    <FormControlLabel
                      control={<Checkbox checked={atPdf} onChange={(e) => setAtPdf(e.target.checked)} size="small" style={{ color: '#548c2f' }} />}
                      label={<Typography style={{ fontSize: 13 }}>PDF Attachment</Typography>}
                    />
                    <FormControlLabel
                      control={<Checkbox checked={atDashboardLink} onChange={(e) => setAtDashboardLink(e.target.checked)} size="small" style={{ color: '#548c2f' }} />}
                      label={<Typography style={{ fontSize: 13 }}>Dashboard Link</Typography>}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions style={{ padding: '12px 20px' }}>
            <Button onClick={() => setReportHistoryOpen(false)} style={{ textTransform: 'none' }}>
              Close
            </Button>
            {autoTrackTab === 'settings' && (
              <Button
                variant="contained"
                onClick={handleSaveAutoTrackSettings}
                disabled={autoTrackSaving}
                style={{
                  textTransform: 'none', fontWeight: 600,
                  backgroundColor: '#548c2f', color: '#fff', boxShadow: 'none',
                }}
              >
                {autoTrackSaving ? 'Saving…' : 'Save Settings'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
  );
}
